import React, { useEffect, useState } from 'react';
//import "@fortawesome/fontawesome-free/css/all.min.css";
import { Button, Col, Form, Row } from 'react-bootstrap';
import 'react-quill/dist/quill.snow.css';

import DataApi from '../../api/DataApi';
import { theme } from '../../theme/theme.js';
import Validator from '../../utils/validator';
import SalesforceToast from '../common/SalesforceToast';
import './css/EditLayout.css';
import './css/EditLayoutModal.css';
import LookupLayout from './LookupLayout';
import { fetchRoleRecordviewIdAllowlistForObject } from './RecordViewNewRecordPicker';
import MultiSelectDropdown from './MultiSelectDropdown1';
import CustomRichTextEditor from '../common/CustomRichTextEditor';
import { isFieldVisible, isFieldRequired } from '../../utils/taskFieldVisibility';
import { isMailConfigFieldVisible, isMailConfigFieldRequired } from '../../utils/mailConfigFieldVisibility';
import { hasPackage } from '../../utils/entitlements';
import OpportunityLineItemApi from '../../api/OpportunityLineItemApi';
import QuoteLineItemApi from '../../api/QuoteLineItemApi';
import { getUserPreferences } from '../../utils/formatUtils';
import moment from 'moment-timezone';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import SystemSettingsApi from '../../api/SystemSettingsApi';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import CurrencyApi from '../../api/CurrencyApi';
import jwt_decode from 'jwt-decode';
import * as constants from '../../constants/CONSTANT';
import { getUserFieldPermissionsByObject, getFlsEditDeniedLowerSet } from '../../utils/permissionCache';

/** Parse system setting value to boolean (e.g. multi_currency_enabled). */
function parseBoolean(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return false;
}

/**
 * Load currency dropdown options for the layout currency field.
 * - If multi_currency is not enabled or currency_exchange_rate list is empty: show only company currency.
 * - Otherwise: show currencies from currency_exchange_rate; is_default is the default selected.
 * @returns {Promise<{ options: Array<{ key: string, value: string, is_default: boolean }>, defaultCode: string }>}
 */
async function loadCurrencyOptionsForLayout() {
  const settingsApi = new SystemSettingsApi();
  const currencyApi = new CurrencyApi();
  let companyCurrency = '';
  let multiCurrencyEnabled = false;
  try {
    const settingsRes = await settingsApi.fetchSettings();
    const settingsData = settingsRes?.data;
    if (settingsData?.success && Array.isArray(settingsData.data)) {
      const companyItem = settingsData.data.find((s) => s && s.key === 'company_currency');
      companyCurrency = (companyItem && companyItem.value) ? String(companyItem.value).trim() : '';
      const multiItem = settingsData.data.find((s) => s && s.key === 'multi_currency_enabled');
      multiCurrencyEnabled = multiItem ? parseBoolean(multiItem.value) : false;
    }
  } catch (e) {
    console.warn('EditLayout: failed to load system settings for currency', e?.message);
  }

  const options = [];
  let defaultCode = companyCurrency || '';

  if (!multiCurrencyEnabled) {
    if (companyCurrency) {
      options.push({ key: companyCurrency, value: companyCurrency, is_default: true });
    }
    return { options, defaultCode };
  }

  try {
    const currencyRes = await currencyApi.fetchAll();
    const list = currencyRes?.data?.data;
    if (!Array.isArray(list) || list.length === 0) {
      if (companyCurrency) {
        options.push({ key: companyCurrency, value: companyCurrency, is_default: true });
      }
      return { options, defaultCode };
    }
    const defaultRow = list.find((r) => r.is_default === true);
    defaultCode = (defaultRow && defaultRow.currency_code) ? String(defaultRow.currency_code).trim() : (companyCurrency || (list[0] && list[0].currency_code) || '');
    list.forEach((row) => {
      const code = row.currency_code ? String(row.currency_code).trim() : '';
      if (!code) return;
      const label = [row.symbol, row.currency_code, row.currency_name].filter(Boolean).join(' - ') || code;
      options.push({
        key: code,
        value: label,
        is_default: !!row.is_default,
      });
    });
    return { options, defaultCode };
  } catch (e) {
    console.warn('EditLayout: failed to load currency list', e?.message);
    if (companyCurrency) {
      options.push({ key: companyCurrency, value: companyCurrency, is_default: true });
    }
    return { options, defaultCode };
  }
}

const EditLayout = ({
  objectName,
  id,
  cloneFromId,
  onSave,
  onSaveAndNew,
  onCancel,
  hideHeader,
  formRef,
  sourcePage,
  queryParams,
  openedInModal = false,
}) => {
  const [formData, setFormData] = useState({});
  const [recordData, setRecordData] = useState(null);
  const [labelData, setLabelData] = useState({});
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [selected, setSelected] = useState();
  const [errors, setErrors] = useState({});
  const [fieldMetaByName, setFieldMetaByName] = useState({});
  const [picklistValues, setPicklistValues] = useState({});
  const [picklistDependencies, setPicklistDependencies] = useState([]);
  const [dependentPicklistValues, setDependentPicklistValues] = useState({});
  const [formDefaults, setFormDefaults] = useState({});
  // Track visible password fields (by field name)
  const [visiblePasswordFields, setVisiblePasswordFields] = useState(new Set());

  // Related Object functionality for task object
  const [availableObjects, setAvailableObjects] = useState([]);
  const [allObjectsData, setAllObjectsData] = useState({}); // Store all objects and their fields
  const [relatedRecordCache, setRelatedRecordCache] = useState({}); // Cache related_record_id values by related_object
  const [relatedObjectEmailFields, setRelatedObjectEmailFields] = useState([]); // Email fields from related object
  const [relatedObjectPhoneFields, setRelatedObjectPhoneFields] = useState([]); // Phone fields from related object

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success',
  });

  // Track if form has been submitted to show errors
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Track if form is currently saving
  const [isSaving, setIsSaving] = useState(false);

  // Attendees state for Meeting tasks
  const [attendees, setAttendees] = useState([]);

  // Track if opportunity/quote has products (to disable currency field)
  const [hasProducts, setHasProducts] = useState(false);

  /** Non-null array → restrict recordview lookup to these ids (role.permission_config); null → no extra filter */
  const [roleRecordviewIdAllowlist, setRoleRecordviewIdAllowlist] = useState(null);

  //console.log("$$ queryParams", queryParams);
  //console.log("## sourcePage", sourcePage);

  const fetchRecord = async () => {
    setSubmitError(null);

    try {
      const dataApi = new DataApi(`object/edit/${objectName}`);

      // Use cloneFromId if this is a clone operation, otherwise use id
      const recordId = cloneFromId || id;

      const qs = new URLSearchParams();
      if (queryParams && queryParams.targetField && queryParams.targetId) {
        qs.set('targetfield', queryParams.targetField);
        qs.set('targetid', queryParams.targetId);
        if (queryParams.parentObjectName) {
          qs.set('parentobjectname', queryParams.parentObjectName);
        }
      }
      if (queryParams && queryParams.editLayoutId) {
        qs.set('editLayoutId', String(queryParams.editLayoutId));
      }
      if (queryParams && queryParams.recordviewId) {
        qs.set('recordviewId', String(queryParams.recordviewId));
      }
      if (queryParams && queryParams.layoutConfigType) {
        qs.set('layoutConfigType', String(queryParams.layoutConfigType));
      }
      const qsStr = qs.toString();
      let idWithParams = recordId;
      if (qsStr) {
        idWithParams = recordId ? `${recordId}?${qsStr}` : `?${qsStr}`;
      }
      if (qsStr) {
        console.log('Using ID with query params:', idWithParams);
      }

      const response = await dataApi.fetchById(idWithParams);
      const data = response?.data?.data;
      console.log('## data', data);
      console.log('data type:', typeof data);

      // Check if the response is a string (error message from backend)
      if (typeof data === 'string') {
        console.log('Received error message from backend:', data);
        setError(data); // This will show "Permission denied!" or other error messages
        return;
      }

      if (!data || Object.keys(data).length === 0) {
        setError('No Record Found!');
      } else {
        const newFormDefaults = {};
        const labels = {};

        // Handle both data structures: direct array or object with details
        let sections = [];
        if (Array.isArray(data)) {
          // Direct array of sections
          sections = data;
          console.log(
            'Using direct array structure with',
            sections.length,
            'sections'
          );
        } else if (data.details && Array.isArray(data.details)) {
          // Object with details property
          sections = data.details;
          console.log(
            'Using details property with',
            sections.length,
            'sections'
          );
        } else {
          console.error('Invalid data structure:', data);
          setError('Invalid data structure received');
          return;
        }

        // Log section names to identify duplicates
        const sectionNames = sections.map(s => s.sectionName);
        console.log('## Section names:', sectionNames);
        const uniqueSectionNames = [...new Set(sectionNames)];
        console.log('## Unique section names:', uniqueSectionNames);

        // Log section labels for display
        const sectionLabels = sections.map(s => ({
          name: s.sectionName,
          label: s.sectionLabel,
        }));
        console.log('## Section labels:', sectionLabels);

        if (sectionNames.length !== uniqueSectionNames.length) {
          console.warn(
            '##Duplicate sections detected:',
            sectionNames.filter(
              (name, index) => sectionNames.indexOf(name) !== index
            )
          );

          // Remove duplicate sections by keeping only the first occurrence
          const seenSections = new Set();
          sections = sections.filter(section => {
            if (seenSections.has(section.sectionName)) {
              console.log(
                '## Removing duplicate section:',
                section.sectionName
              );
              return false;
            }
            seenSections.add(section.sectionName);
            return true;
          });
          console.log('##After deduplication:', sections.length, 'sections');
        }

        // Flatten and de-duplicate fields by fieldName
        console.log('## Processing sections:', sections);
        const flatFields = sections.flatMap(section => section.fields || []);
        console.log('## Flat fields:', flatFields);
        const seenFieldNames = new Set();
        const allFields = flatFields.filter(f => {
          const name = f?.fieldName;
          if (!name) return false;
          if (seenFieldNames.has(name)) return false;
          seenFieldNames.add(name);
          return true;
        });

        const hiddenFromApi =
          !Array.isArray(data) && Array.isArray(data.otherHiddenFields)
            ? data.otherHiddenFields
            : [];
        hiddenFromApi.forEach(f => {
          const name = f?.fieldName;
          if (!name || seenFieldNames.has(name)) return;
          seenFieldNames.add(name);
          allFields.push(f);
        });

        // Debug: Log all fields to see their structure
        console.log(
          '🔍 All fields structure:',
          allFields.map(f => ({
            fieldName: f.fieldName,
            type: f.type,
            precision: f.precision,
            scale: f.scale,
            maxLength: f.maxLength,
            isRequired: f.isRequired,
          }))
        );

        allFields.forEach(f => {
          if (f.type === 'datetime' && f.value) {
            // Convert ISO to datetime-local format in user's timezone
            const prefs = getUserPreferences();
            const userTimezone = prefs.time_zone || 'UTC';

            // Parse the ISO datetime string and convert to user's timezone
            const momentDate = moment(f.value).tz(userTimezone);

            // Format as datetime-local (YYYY-MM-DDTHH:mm)
            const formatted =
              momentDate.format('YYYY-MM-DD') +
              'T' +
              momentDate.format('HH:mm');
            newFormDefaults[f.fieldName] = formatted;
          } else if (f.type === 'date' && f.value) {
            // Format to YYYY-MM-DD in user's timezone
            const prefs = getUserPreferences();
            const userTimezone = prefs.time_zone || 'UTC';

            // Parse the date and convert to user's timezone
            const momentDate = moment(f.value).tz(userTimezone);

            // Format as YYYY-MM-DD
            const formatted = momentDate.format('YYYY-MM-DD');
            newFormDefaults[f.fieldName] = formatted;
          } else if (f.type === 'time' && f.value) {
            // Convert time value to HH:MM format for time input
            let timeValue = f.value;
            // If it's a full datetime string, extract just the time part
            if (timeValue.includes('T') || timeValue.includes(' ')) {
              const d = new Date(timeValue);
              timeValue =
                String(d.getHours()).padStart(2, '0') +
                ':' +
                String(d.getMinutes()).padStart(2, '0');
            } else if (timeValue.includes(':')) {
              // If it's already in HH:MM:SS format, extract just HH:MM
              const parts = timeValue.split(':');
              timeValue = parts[0] + ':' + parts[1];
            }
            newFormDefaults[f.fieldName] = timeValue;
          } else if (f.type === 'boolean' || f.type === 'checkbox') {
            // Preserve boolean values; default to false when null/undefined
            newFormDefaults[f.fieldName] = Boolean(f.value);
          } else if (f.type === 'password') {
            // For password fields: always show empty (user must enter new password to change)
            newFormDefaults[f.fieldName] = ''; // Empty value in form
          } else {
            // Use nullish coalescing to preserve 0/false
            newFormDefaults[f.fieldName] = f.value ?? '';
            console.log(
              `🔍 Field ${f.fieldName}: value=${f.value}, type=${f.type}, isDependent=${isDependentField(f.fieldName)}`
            );
          }

          //formDefaults[f.fieldName] = f.value || "";
          if (f.type === 'lookup') {
            labels[f.fieldName] = f.displayValue || '';
            // Debug owner fields
            if (
              f.fieldName?.toLowerCase().includes('owner') ||
              f.fieldName === 'ownerid'
            ) {
              console.log(
                `Owner field ${f.fieldName}: value=${f.value}, displayValue=${f.displayValue}`
              );
            }
          }
        });

        // Build canonical field meta across sections to resolve duplicates
        const metaMap = {};
        allFields.forEach(f => {
          const name = f.fieldName;
          if (!name) return;
          const existing = metaMap[name] || {};
          const normalizedType = (f.type || 'text').toLowerCase();
          const prioritizedType = [existing.type, normalizedType].includes(
            'checkbox'
          )
            ? 'checkbox'
            : [existing.type, normalizedType].includes('boolean')
              ? 'boolean'
              : normalizedType || existing.type || 'text';
          metaMap[name] = {
            ...existing,
            type: prioritizedType,
            label: f.label || existing.label || name, // Add label to metaMap
            isRequired: Boolean(existing.isRequired || f.isRequired),
            isReadonly: Boolean(existing.isReadonly || f.isReadonly),
            columnSpan: f.columnSpan || existing.columnSpan || 1,
          };
        });

        let flsEditDeniedLower = new Set();
        try {
          const flsByObj = await getUserFieldPermissionsByObject();
          flsEditDeniedLower = getFlsEditDeniedLowerSet(flsByObj, objectName);
        } catch (flsErr) {
          console.warn('EditLayout: could not load field-level edit permissions', flsErr?.message || flsErr);
        }
        if (flsEditDeniedLower.size > 0) {
          for (const name of Object.keys(metaMap)) {
            if (flsEditDeniedLower.has(String(name).trim().toLowerCase())) {
              metaMap[name] = {
                ...metaMap[name],
                isReadonly: true,
                flsNoEdit: true,
              };
            }
          }
        }

        // Store the defaults but don't apply them yet - we'll filter out dependent fields after dependencies are loaded
        console.log('🔍 Setting formDefaults with values:', newFormDefaults);
        console.log('🔍 Is editing existing record (has id):', !!id);
        setFormDefaults(newFormDefaults);
        setLabelData(labels);
        setRecordData({
          ...data,
          details: sections,
          otherHiddenFields: hiddenFromApi,
        });
        setFieldMetaByName(metaMap);

        // Initially set form data with all defaults - we'll filter out dependent fields later
        setFormData(newFormDefaults);

        if (
          objectName === 'lead' &&
          !cloneFromId &&
          !id &&
          queryParams?.inboundPhone
        ) {
          const phoneVal = String(queryParams.inboundPhone).trim();
          if (phoneVal) {
            const fieldName =
              queryParams.inboundPhoneField ||
              allFields.find(
                f =>
                  String(f.type || '').toLowerCase() === 'phone' ||
                  String(f.fieldName || '').toLowerCase() === 'phone'
              )?.fieldName ||
              'phone';
            setFormData(prev => ({
              ...prev,
              [fieldName]: phoneVal,
            }));
            console.log(
              `[EditLayout] Pre-filled lead field ${fieldName} from inbound caller`
            );
          }
        }

        if (objectName === 'lead' && !cloneFromId && !id) {
          const inboundLeadSource =
            queryParams?.inboundLeadSource != null &&
            String(queryParams.inboundLeadSource).trim() !== ''
              ? String(queryParams.inboundLeadSource).trim()
              : null;
          const inboundLeadStatus =
            queryParams?.inboundLeadStatus != null &&
            String(queryParams.inboundLeadStatus).trim() !== ''
              ? String(queryParams.inboundLeadStatus).trim()
              : null;

          if (inboundLeadSource || inboundLeadStatus) {
            const lowerToFieldName = new Map(
              allFields
                .map(f => String(f.fieldName || '').trim())
                .filter(Boolean)
                .map(n => [n.toLowerCase(), n])
            );
            const leadSourceField =
              lowerToFieldName.get('lead_source') || 'lead_source';
            const leadStatusField =
              lowerToFieldName.get('lead_status') || 'lead_status';

            setFormData(prev => ({
              ...prev,
              ...(inboundLeadSource &&
              (!prev?.[leadSourceField] ||
                String(prev?.[leadSourceField]).trim() === '')
                ? { [leadSourceField]: inboundLeadSource }
                : {}),
              ...(inboundLeadStatus &&
              (!prev?.[leadStatusField] ||
                String(prev?.[leadStatusField]).trim() === '')
                ? { [leadStatusField]: inboundLeadStatus }
                : {}),
            }));
          }
        }

        // Special handling for task object when coming from related list or with default values
        if (objectName === 'task' && queryParams) {
          const taskDefaults = {};

          // Pre-populate related_object field with the source object name
          if (queryParams.parentObjectName) {
            taskDefaults.related_object = queryParams.parentObjectName;
            console.log(`Pre-populating related_object for task: ${queryParams.parentObjectName}`);
          }

          // Apply default field values if provided in queryParams
          // These are useful when creating tasks from other contexts
          if (queryParams.defaultTitle) {
            taskDefaults.title = queryParams.defaultTitle;
            taskDefaults.subject = queryParams.defaultTitle; // Support both field names
            console.log(`Setting default title: ${queryParams.defaultTitle}`);
          }

          if (queryParams.defaultDueDate) {
            taskDefaults.due_date = queryParams.defaultDueDate;
            taskDefaults.targetdate = queryParams.defaultDueDate; // Support both field names
            console.log(`Setting default due date: ${queryParams.defaultDueDate}`);
          }

          if (queryParams.defaultStartDateTime || queryParams.defaultMeetingStartDateTime) {
            const startValue = queryParams.defaultStartDateTime || queryParams.defaultMeetingStartDateTime;
            taskDefaults.start_date_time = startValue; // Backend field name
            taskDefaults.start_datetime = startValue;
            taskDefaults.startdatetime = startValue;
            taskDefaults.meeting_start_datetime = startValue; // Support meeting-specific field
            console.log(`Setting default start datetime: ${startValue}`);
          }

          if (queryParams.defaultEndDateTime || queryParams.defaultMeetingEndDateTime) {
            const endValue = queryParams.defaultEndDateTime || queryParams.defaultMeetingEndDateTime;
            taskDefaults.end_date_time = endValue; // Backend field name
            taskDefaults.end_datetime = endValue;
            taskDefaults.enddatetime = endValue;
            taskDefaults.meeting_end_datetime = endValue; // Support meeting-specific field
            console.log(`Setting default end datetime: ${endValue}`);
          }

          // Pre-select type field (e.g., "Meeting", "Call", "Follow-up")
          if (queryParams.taskType) {
            taskDefaults.type = queryParams.taskType;
            console.log(`Pre-selecting task type: ${queryParams.taskType}`);
          }

          // Apply all task defaults at once
          if (Object.keys(taskDefaults).length > 0) {
            setFormData(prev => ({
              ...prev,
              ...taskDefaults,
            }));
            console.log('Applied task defaults:', taskDefaults);
            console.log('Query params:', queryParams);
          }
        }

        // Pre-fill note parent / related_object from query (e.g. Activity widget → Add Note)
        if (objectName === 'note' && queryParams && !id && !cloneFromId) {
          const noteDefaults = {};
          if (queryParams.targetField && queryParams.targetId) {
            noteDefaults[queryParams.targetField] = queryParams.targetId;
          }
          if (queryParams.parentObjectName) {
            noteDefaults.related_object = queryParams.parentObjectName;
          }
          if (Object.keys(noteDefaults).length > 0) {
            setFormData(prev => ({
              ...prev,
              ...noteDefaults,
            }));
            console.log('Applied note defaults from queryParams:', noteDefaults);
          }
        }

        // Check for picklist fields and their values
        console.log(
          'All fields for picklist check:',
          allFields.map(f => ({
            fieldName: f.fieldName,
            type: f.type,
            picklistValues: f.picklistValues,
            allKeys: Object.keys(f),
          }))
        );

        // First, let's see if there are any picklist fields at all
        const allPicklistFields = allFields.filter(
          f => f.type === 'picklist' || f.type === 'multipicklist'
        );
        console.log('All picklist fields found:', allPicklistFields);

        // Let's also check what field types we have
        const fieldTypes = [...new Set(allFields.map(f => f.type))];
        console.log('All field types found:', fieldTypes);

        // Load currency dropdown options when layout has a currency field (company currency + multi_currency / currency_exchange_rate)
        let currencyOptionsForForm = null;
        if (allPicklistFields.some((f) => f.fieldName?.toLowerCase() === 'currency')) {
          try {
            currencyOptionsForForm = await loadCurrencyOptionsForLayout();
          } catch (err) {
            console.warn('EditLayout: loadCurrencyOptionsForLayout failed', err?.message);
          }
        }

        const picklistValuesMap = {};

        // Process picklist fields - use picklistValues from the field data directly
        for (const field of allPicklistFields) {
          console.log('## Processing picklist field:', field.fieldName);
          console.log('## field.picklistValues:', field.picklistValues);
          console.log(
            '## field.picklistValues type:',
            typeof field.picklistValues
          );
          console.log(
            '## field.picklistValues isArray:',
            Array.isArray(field.picklistValues)
          );

          // Currency field: dropdown from company currency (system setting) or currency_exchange_rate when multi-currency enabled
          if (field.fieldName?.toLowerCase() === 'currency' && currencyOptionsForForm) {
            picklistValuesMap[field.fieldName] = currencyOptionsForForm.options;
            if ((!newFormDefaults[field.fieldName] || newFormDefaults[field.fieldName] === '') && !id && currencyOptionsForForm.defaultCode) {
              newFormDefaults[field.fieldName] = currencyOptionsForForm.defaultCode;
            }
            continue;
          }

          // Use the picklist values directly from the field data
          if (field.picklistValues && Array.isArray(field.picklistValues)) {
            console.log(
              `Using field data picklist values for ${field.fieldName}:`,
              field.picklistValues
            );
            picklistValuesMap[field.fieldName] = field.picklistValues;

            // Set default value if there's a default option
            // BUT ONLY for NEW records and ONLY for fields that are not dependent
            if (
              !newFormDefaults[field.fieldName] ||
              newFormDefaults[field.fieldName] === ''
            ) {
              const defaultOption = field.picklistValues.find(
                opt => opt.is_default
              );
              if (defaultOption) {
                // Only set default for new records (not when editing existing records)
                if (!id) {
                  console.log(
                    `Setting default value for ${field.fieldName}:`,
                    defaultOption.key
                  );
                  newFormDefaults[field.fieldName] = defaultOption.key;
                } else {
                  console.log(
                    `Skipping default value for ${field.fieldName} (editing existing record):`,
                    defaultOption.key
                  );
                }
              } else {
                console.log(
                  `No default option found for ${field.fieldName} in field data`
                );
              }
            } else {
              console.log(
                `Field ${field.fieldName} already has a default value:`,
                newFormDefaults[field.fieldName]
              );
            }
          } else if (
            field.picklistValues &&
            typeof field.picklistValues === 'string'
          ) {
            // Handle case where picklist values might be a JSON string
            try {
              const parsedValues = JSON.parse(field.picklistValues);
              if (Array.isArray(parsedValues)) {
                console.log(
                  `Parsed picklist values for field ${field.fieldName}:`,
                  parsedValues
                );
                picklistValuesMap[field.fieldName] = parsedValues;

                // Set default value if there's a default option
                // BUT ONLY for NEW records and ONLY for fields that are not dependent
                if (
                  !newFormDefaults[field.fieldName] ||
                  newFormDefaults[field.fieldName] === ''
                ) {
                  const defaultOption = parsedValues.find(
                    opt => opt.is_default
                  );
                  if (defaultOption) {
                    // Only set default for new records (not when editing existing records)
                    if (!id) {
                      console.log(
                        `Setting default value for ${field.fieldName}:`,
                        defaultOption.key
                      );
                      newFormDefaults[field.fieldName] = defaultOption.key;
                    } else {
                      console.log(
                        `Skipping default value for ${field.fieldName} (editing existing record):`,
                        defaultOption.key
                      );
                    }
                  } else {
                    console.log(
                      `No default option found for ${field.fieldName} in field data`
                    );
                  }
                } else {
                  console.log(
                    `Field ${field.fieldName} already has a default value:`,
                    newFormDefaults[field.fieldName]
                  );
                }
              }
            } catch (parseError) {
              console.log(
                `Failed to parse picklist values for field ${field.fieldName}:`,
                parseError
              );
              picklistValuesMap[field.fieldName] = [];
            }
          } else {
            console.log(
              `No picklist values found for field ${field.fieldName} in field data`
            );
            picklistValuesMap[field.fieldName] = [];
          }
        }

        setPicklistValues(picklistValuesMap);
        console.log('Final picklistValuesMap:', picklistValuesMap);
        console.log(
          'Fields with picklist values:',
          Object.keys(picklistValuesMap)
        );
        Object.keys(picklistValuesMap).forEach(fieldName => {
          console.log(
            `Field ${fieldName} has ${picklistValuesMap[fieldName]?.length || 0} picklist values`
          );
        });

        // Store the defaults but don't apply them yet - we'll do this after dependencies are loaded
        console.log(
          'Picklist defaults prepared (not applied yet):',
          newFormDefaults
        );
        console.log('About to fetch dependencies...');

        // Fetch picklist field dependencies for this object
        try {
          console.log(
            'Fetching picklist field dependencies for object:',
            objectName
          );
          const dependenciesApi = new DataApi(
            `picklist/field-dependencies/object/${objectName}`
          );
          const dependenciesResponse = await dependenciesApi.fetchAll();

          console.log('@@ RAW dependenciesResponse:', dependenciesResponse);
          console.log(
            '@@ dependenciesResponse.data:',
            dependenciesResponse.data
          );
          console.log(
            '@@ dependenciesResponse.data.success:',
            dependenciesResponse.data?.success
          );
          console.log(
            '@@ dependenciesResponse.data.data:',
            dependenciesResponse.data?.data
          );
          console.log(
            '@@ dependenciesResponse.data.data.length:',
            dependenciesResponse.data?.data?.length
          );

          if (dependenciesResponse?.data?.success) {
            console.log(
              'Picklist dependencies fetched successfully:',
              dependenciesResponse.data.data
            );
            console.log(
              '@@ About to call setPicklistDependencies with:',
              dependenciesResponse.data.data
            );
            console.log(
              '🔍 Raw dependencies data from backend:',
              dependenciesResponse.data.data
            );
            console.log(
              '🔍 First dependency structure:',
              dependenciesResponse.data.data[0]
            );
            if (dependenciesResponse.data.data[0]) {
              console.log(
                '🔍 First dependency controlling_field_details:',
                dependenciesResponse.data.data[0].controlling_field_details
              );
              console.log(
                '🔍 First dependency dependent_field_details:',
                dependenciesResponse.data.data[0].dependent_field_details
              );
              console.log(
                '🔍 First dependency value_dependencies:',
                dependenciesResponse.data.data[0].value_dependencies
              );
            }
            setPicklistDependencies(dependenciesResponse.data.data);
            console.log('@@ setPicklistDependencies called');

            // Debug: Log which fields are dependent
            const dependentFields = [];
            const controllingFields = [];
            dependenciesResponse.data.data.forEach(dep => {
              dependentFields.push(dep.dependent_field_details?.name);
              controllingFields.push(dep.controlling_field_details?.name);
            });
            console.log('@@ Dependent fields found:', dependentFields);
            console.log('@@ Controlling fields found:', controllingFields);

            // Note: Defaults will be applied in useEffect when picklistDependencies are loaded
          } else {
            console.warn(
              'Failed to fetch picklist dependencies:',
              dependenciesResponse?.data?.message
            );
            // Note: Defaults will be applied in useEffect when picklistDependencies are loaded
          }
        } catch (dependenciesError) {
          console.warn(
            'Error fetching picklist dependencies:',
            dependenciesError
          );
          // Note: Defaults will be applied in useEffect when picklistDependencies are loaded
        }
      }
    } catch (error) {
      console.error('Failed to fetch record:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'An unexpected error occurred.';
      setError(message);
    } finally {
      setIsLoaded(true);
    }
  };

  // API functions for Related Object functionality
  const fetchAvailableObjects = async () => {
    try {
      const dataApi = new DataApi('objectconfig');
      const response = await dataApi.fetchAll();
      console.log('## response', response);
      if (response?.data?.success) {
        const data = response.data.data;
        console.log('## Raw data from API:', data);
        console.log('## Data length:', data?.length);

        // Extract unique objects for the dropdown
        const uniqueObjects = [];
        const objectsMap = {};

        data.forEach(item => {
          if (!objectsMap[item.object_name]) {
            objectsMap[item.object_name] = true;
            uniqueObjects.push({
              object_name: item.object_name,
              object_label: item.object_label,
            });
          }
        });

        // Sort objects by object_label alphabetically
        uniqueObjects.sort((a, b) => {
          const labelA = a.object_label || a.object_name || '';
          const labelB = b.object_label || b.object_name || '';
          return labelA.localeCompare(labelB);
        });

        console.log(
          '## Unique objects (sorted by object_label):',
          uniqueObjects
        );
        setAvailableObjects(uniqueObjects);
        setAllObjectsData(data); // Store all data for field filtering
        console.log('## All objects data set:', data);
      }
    } catch (error) {
      console.error('Error fetching available objects:', error);
    }
  };

  const queryParamsKey = JSON.stringify({
    t: queryParams?.targetField,
    tid: queryParams?.targetId,
    p: queryParams?.parentObjectName,
    el: queryParams?.editLayoutId,
    rv: queryParams?.recordviewId,
    lc: queryParams?.layoutConfigType,
  });

  useEffect(() => {
    fetchRecord();
  }, [objectName, id, cloneFromId, queryParamsKey]);

  // Check if opportunity/quote has products (to disable currency field)
  useEffect(() => {
    const checkProducts = async () => {
      if ((objectName === 'opportunity' || objectName === 'quote') && id && !cloneFromId) {
        try {
          let response;
          if (objectName === 'opportunity') {
            response = await OpportunityLineItemApi.fetchByOpportunity(id);
          } else {
            response = await QuoteLineItemApi.fetchByQuote(id);
          }

          if (response?.success && response?.data) {
            const products = Array.isArray(response.data) ? response.data : [];
            setHasProducts(products.length > 0);
            console.log(`Found ${products.length} products for ${objectName} ${id}`);
          } else {
            setHasProducts(false);
          }
        } catch (error) {
          console.error(`Failed to check products for ${objectName}:`, error);
          setHasProducts(false);
        }
      } else {
        // Reset for new records or other objects
        setHasProducts(false);
      }
    };

    checkProducts();
  }, [objectName, id, cloneFromId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const on = objectName && String(objectName).trim();
      if (!on || on.toLowerCase() === 'email_log') {
        if (!cancelled) setRoleRecordviewIdAllowlist(null);
        return;
      }
      const ids = await fetchRoleRecordviewIdAllowlistForObject(on);
      if (!cancelled) setRoleRecordviewIdAllowlist(ids);
    })();
    return () => {
      cancelled = true;
    };
  }, [objectName]);

  // Load attendees for existing tasks (including when cloning) - for all task types
  useEffect(() => {
    const loadAttendees = async () => {
      // Load attendees for existing tasks or when cloning
      const recordId = cloneFromId || id;
      if (objectName === 'task' && recordId) {
        try {
          const dataApi = new DataApi('object/list/attendee');
          const queryParams = new URLSearchParams({ filter0: `event:equals:${recordId}` }).toString();
          const response = await dataApi.fetchAll(queryParams);
          if (response?.data?.data && Array.isArray(response.data.data)) {
            // Load user labels for attendees that have userid
            const attendeesWithUserLabels = await Promise.all(
              response.data.data.map(async (attendee) => {
                // Extract values from field objects if needed (fetchRecords format)
                const extractValue = (field) => {
                  if (typeof field === 'object' && field !== null && 'value' in field) {
                    return field.value;
                  }
                  return field;
                };

                // Extract displayValue from field objects (for lookup fields)
                const extractDisplayValue = (field) => {
                  if (typeof field === 'object' && field !== null && 'displayValue' in field) {
                    return field.displayValue || '';
                  }
                  return '';
                };

                // Handle userid as lookup field - check if it's already in lookup format
                let userIdValue = null;
                let userDisplayLabel = '';

                if (attendee.userid) {
                  if (typeof attendee.userid === 'object' && attendee.userid !== null) {
                    // Already in lookup field format (from fetchRecords)
                    userIdValue = attendee.userid.value || null;
                    userDisplayLabel = attendee.userid.displayValue || '';
                  } else {
                    // Plain value - extract normally
                    userIdValue = attendee.userid;
                  }
                }

                // Normalize attendee data
                const normalizedAttendee = {
                  id: extractValue(attendee.id),
                  name: extractValue(attendee.name) || '',
                  email: extractValue(attendee.email) || '',
                  mobile: extractValue(attendee.mobile) || '',
                  userid: userIdValue,
                  userLabel: userDisplayLabel,
                };

                // When cloning, remove the ID so it becomes a new attendee
                const attendeeData = cloneFromId
                  ? { ...normalizedAttendee, id: null }
                  : normalizedAttendee;

                // If we have userid but no display label, fetch the user to get the label
                if (attendeeData.userid && !attendeeData.userLabel) {
                  try {
                    const userApi = new DataApi('object/edit/user');
                    const userResponse = await userApi.fetchById(attendeeData.userid);
                    if (userResponse?.data?.data) {
                      // Extract user data - handle fetchById format (fields array structure)
                      const userData = userResponse.data.data;
                      let userLabel = '';

                      // fetchById returns data in details/fields structure
                      if (userData.details && Array.isArray(userData.details)) {
                        const allFields = userData.details.flatMap(s => s.fields || []);
                        const firstnameField = allFields.find(f => f.fieldName === 'firstname' || f.fieldName === 'first_name');
                        const lastnameField = allFields.find(f => f.fieldName === 'lastname' || f.fieldName === 'last_name');
                        const emailField = allFields.find(f => f.fieldName === 'email');

                        const firstname = firstnameField?.value || '';
                        const lastname = lastnameField?.value || '';
                        const email = emailField?.value || '';

                        userLabel = `${firstname} ${lastname}`.trim() || email || '';
                      } else {
                        // Fallback: try direct format (shouldn't happen with fetchById but just in case)
                        const firstname = userData.firstname || userData.first_name || '';
                        const lastname = userData.lastname || userData.last_name || '';
                        const email = userData.email || '';
                        userLabel = `${firstname} ${lastname}`.trim() || email || '';
                      }

                      if (userLabel) {
                        return { ...attendeeData, userLabel };
                      }
                    }
                  } catch (userError) {
                    console.warn(`Failed to load user ${attendeeData.userid} for attendee:`, userError);
                  }
                }
                return attendeeData;
              })
            );
            setAttendees(attendeesWithUserLabels);
            console.log('Loaded attendees:', attendeesWithUserLabels);
          }
        } catch (error) {
          console.error('Failed to load attendees:', error);
        }
      }
    };
    loadAttendees();
  }, [objectName, id, cloneFromId]);

  // Initialize available objects for task object
  useEffect(() => {
    if (objectName === 'task') {
      fetchAvailableObjects();
    }
  }, [objectName]);

  // Fetch email and phone fields from related object when checkbox is checked and related_object is selected
  useEffect(() => {
    const fetchRelatedObjectFields = async () => {
      if (
        objectName === 'task' &&
        formData.send_notification_to_related_record &&
        formData.related_object
      ) {
        try {
          const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
            formData.related_object
          );

          if (response.success && response.data && response.data.fields) {
            const fields = response.data.fields;

            // Filter email fields
            const emailFields = fields.filter(
              f => f.data_type?.toLowerCase() === 'email'
            );

            // Filter phone fields (phone, mobile, etc.)
            const phoneFields = fields.filter(
              f => {
                const dataType = f.data_type?.toLowerCase();
                const fieldName = f.field_name?.toLowerCase() || '';
                return dataType === 'phone' ||
                       fieldName.includes('phone') ||
                       fieldName.includes('mobile');
              }
            );

            setRelatedObjectEmailFields(emailFields);
            setRelatedObjectPhoneFields(phoneFields);

            console.log('Fetched email fields:', emailFields);
            console.log('Fetched phone fields:', phoneFields);
          } else {
            setRelatedObjectEmailFields([]);
            setRelatedObjectPhoneFields([]);
          }
        } catch (error) {
          console.error('Error fetching related object fields:', error);
          setRelatedObjectEmailFields([]);
          setRelatedObjectPhoneFields([]);
        }
      } else {
        // Clear fields when checkbox is unchecked or related_object is not selected
        setRelatedObjectEmailFields([]);
        setRelatedObjectPhoneFields([]);
      }
    };

    fetchRelatedObjectFields();
  }, [objectName, formData.send_notification_to_related_record, formData.related_object]);

  // Initialize tooltips when component mounts
  useEffect(() => {
    // Check if Bootstrap is available, if not, use native tooltips
    if (window.bootstrap && window.bootstrap.Tooltip) {
      // Initialize Bootstrap tooltips for dependency indicators
      const tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]')
      );
      const tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });

      // Cleanup tooltips when component unmounts
      return () => {
        tooltipList.forEach(tooltip => tooltip.dispose());
      };
    } else {
      // Fallback: Native tooltips will work with the title attribute
      console.log('Bootstrap not available, using native tooltips');
    }
  }, [picklistDependencies]); // Re-initialize when dependencies change

  // Consolidated effect to handle all dependent picklist logic
  useEffect(() => {
    console.log(
      '$$ Consolidated picklistDependencies useEffect : ',
      picklistDependencies
    );

    if (picklistDependencies.length > 0) {
      console.log('Picklist dependencies updated:', picklistDependencies);

      // Log detailed information about each dependency
      picklistDependencies.forEach((dep, index) => {
        console.log(`Dependency ${index + 1}:`, {
          controlling_field: dep.controlling_field,
          dependent_field: dep.dependent_field,
          controlling_field_details: dep.controlling_field_details,
          dependent_field_details: dep.dependent_field_details,
          value_dependencies: dep.value_dependencies,
        });
      });

      // Update dependent picklist values for all dependent fields
      const updateDependentValues = async () => {
        const newDependentValues = {};

        for (const dep of picklistDependencies) {
          const dependentFieldName = dep.dependent_field_details?.name;
          const controllingFieldName = dep.controlling_field_details?.name;
          const controllingValue = formData[controllingFieldName];

          if (controllingValue) {
            try {
              const availableValues = await getDependentPicklistValues(
                controllingFieldName,
                controllingValue,
                dependentFieldName
              );
              newDependentValues[dependentFieldName] = availableValues;
              console.log(
                `Updated dependent values for ${dependentFieldName} with controlling value ${controllingValue}:`,
                availableValues
              );
            } catch (error) {
              console.error(
                `Error updating dependent values for ${dependentFieldName}:`,
                error
              );
              newDependentValues[dependentFieldName] = [];
            }
          } else {
            // No controlling value selected, show no values
            newDependentValues[dependentFieldName] = [];
            console.log(
              `No controlling value for ${dependentFieldName}, showing no values`
            );
          }
        }

        console.log('$$ newDependentValues: ', newDependentValues);
        setDependentPicklistValues(newDependentValues);
      };

      updateDependentValues();
    }
  }, [picklistDependencies, formData]);

  // Apply picklist defaults when dependencies are loaded and clear dependent field values
  useEffect(() => {
    if (
      picklistDependencies &&
      picklistDependencies.length > 0 &&
      Object.keys(formDefaults).length > 0
    ) {
      console.log(
        'Applying picklist defaults after dependencies loaded:',
        formDefaults
      );
      console.log('Is editing existing record (has id):', !!id);
      console.log('Is cloning (has cloneFromId):', !!cloneFromId);

      // Filter out dependent fields from getting defaults and clear their values
      const filteredDefaults = {};
      const fieldsToClear = {};

      Object.keys(formDefaults).forEach(fieldName => {
        if (isDependentField(fieldName)) {
          console.log(
            `Skipping default for dependent field ${fieldName}: ${formDefaults[fieldName]}`
          );

          // Only clear dependent field values for NEW records (not cloning, not editing)
          if (!id && !cloneFromId) {
            // This is a new record (not cloning) - clear dependent field values
            fieldsToClear[fieldName] = '';
            console.log(
              `Will clear dependent field ${fieldName} value (new record)`
            );
          } else if (cloneFromId) {
            // When cloning, preserve dependent field values from the cloned record
            filteredDefaults[fieldName] = formDefaults[fieldName];
            console.log(
              `Will preserve dependent field ${fieldName} value from cloned record: ${formDefaults[fieldName]}`
            );
          } else {
            // This is an existing record - do nothing, preserve the actual saved value from database
            console.log(
              `Skipping default for dependent field ${fieldName}: ${formDefaults[fieldName]} (editing existing record - preserving saved value)`
            );
          }
        } else {
          // For controlling fields: apply defaults for new records, preserve for cloning/editing
          if (!id && !cloneFromId) {
            // New record (not cloning) - apply defaults
            filteredDefaults[fieldName] = formDefaults[fieldName];
            console.log(
              `Will apply default for controlling field ${fieldName}: ${formDefaults[fieldName]} (new record)`
            );
          } else if (cloneFromId) {
            // When cloning, preserve all field values from the cloned record
            filteredDefaults[fieldName] = formDefaults[fieldName];
            console.log(
              `Will preserve controlling field ${fieldName} value from cloned record: ${formDefaults[fieldName]}`
            );
          } else {
            console.log(
              `Skipping default for controlling field ${fieldName}: ${formDefaults[fieldName]} (editing existing record - preserving saved value)`
            );
          }
        }
      });

      // Apply defaults: for new records, only apply if field is empty; for cloning, apply all to ensure preservation
      const defaultsToApply = {};
      Object.keys(filteredDefaults).forEach(fieldName => {
        // When cloning, apply all values from formDefaults to ensure everything is preserved
        // For new records, only apply if field is empty
        if (cloneFromId || !formData[fieldName] || formData[fieldName] === '') {
          defaultsToApply[fieldName] = filteredDefaults[fieldName];
          console.log(
            `Will apply default for ${fieldName}: ${filteredDefaults[fieldName]}${cloneFromId ? ' (cloning - preserving from source)' : ''}`
          );
        } else {
          console.log(
            `Skipping default for ${fieldName} - already has value: ${formData[fieldName]}`
          );
        }
      });

      // Apply both defaults and cleared values
      const allUpdates = { ...defaultsToApply, ...fieldsToClear };
      if (Object.keys(allUpdates).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...allUpdates,
        }));
        console.log('Applied defaults:', defaultsToApply);
        console.log('Cleared dependent fields:', fieldsToClear);
      } else {
        console.log(
          'No defaults to apply - all fields already have values or are dependent'
        );
      }
    }
  }, [picklistDependencies, formDefaults, id]);

  const handleChange = (e, fieldName) => {
    console.log('$$ handleChange: ', fieldName);
    const newValue = e.target.value;

    setFormData(prev => {
      const newFormData = {
        ...prev,
        [fieldName]: newValue,
      };

      // Special handling for task object: manage related_record_id when related_object changes
      if (objectName === 'task' && fieldName === 'related_object') {
        const previousRelatedObject = prev.related_object;
        const currentRelatedRecordId = prev.related_record_id;
        const currentRelatedRecordLabel = labelData.related_record_id;

        console.log(
          'Related object changed from',
          previousRelatedObject,
          'to',
          newValue
        );

        // Cache the current related_record_id value for the previous object
        if (previousRelatedObject && currentRelatedRecordId) {
          console.log(
            'Caching related_record_id for',
            previousRelatedObject,
            ':',
            currentRelatedRecordId
          );
          setRelatedRecordCache(prevCache => ({
            ...prevCache,
            [previousRelatedObject]: {
              value: currentRelatedRecordId,
              label: currentRelatedRecordLabel,
            },
          }));
        }

        // Check if we have a cached value for the new object
        const cachedValue = relatedRecordCache[newValue];
        if (cachedValue) {
          console.log(
            'Restoring cached related_record_id for',
            newValue,
            ':',
            cachedValue
          );
          newFormData.related_record_id = cachedValue.value;
          setLabelData(prevLabels => ({
            ...prevLabels,
            related_record_id: cachedValue.label,
          }));
        } else {
          console.log(
            'No cached value for',
            newValue,
            ', clearing related_record_id'
          );
          newFormData.related_record_id = '';
          setLabelData(prevLabels => ({
            ...prevLabels,
            related_record_id: '',
          }));
        }
      }

      return newFormData;
    });

    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    console.log('%% picklistDependencies.length', picklistDependencies.length);
    // Handle dependent picklist logic
    if (picklistDependencies && picklistDependencies.length > 0) {
      // Find dependencies where this field is the controlling field
      const controllingDependencies = picklistDependencies.filter(
        dep => dep.controlling_field_details?.name === fieldName
      );

      if (controllingDependencies.length > 0) {
        console.log(
          `%% Field ${fieldName} changed to ${newValue}, updating dependent fields:`,
          controllingDependencies.map(dep => dep.dependent_field_details?.name)
        );

        // For each dependent field, update available options and smart clear values
        controllingDependencies.forEach(async dep => {
          const dependentFieldName = dep.dependent_field_details?.name;

          try {
            const availableValues = await getDependentPicklistValues(
              fieldName,
              newValue,
              dependentFieldName
            );

            console.log(
              `Dependent field ${dependentFieldName} now has ${availableValues.length} available values:`,
              availableValues
            );

            // Update the dependent picklist values state
            setDependentPicklistValues(prev => ({
              ...prev,
              [dependentFieldName]: availableValues,
            }));

            // Smart clearing: Only remove dependent values that are no longer valid
            setFormData(prev => {
              const currentDependentValue = prev[dependentFieldName] || '';
              let newDependentValue = currentDependentValue;

              if (currentDependentValue) {
                // Get the new available values for this dependent field
                const availableKeys = availableValues.map(
                  v => v.key || v.value_key
                );

                console.log(`Smart clearing for ${dependentFieldName}:`);
                console.log(`  Current value: ${currentDependentValue}`);
                console.log(`  Available keys: ${availableKeys}`);

                if (currentDependentValue.includes(';')) {
                  // This is a multipicklist - filter out invalid values
                  const currentValues = currentDependentValue
                    .split(';')
                    .filter(v => v.trim() !== '');
                  const validValues = currentValues.filter(val =>
                    availableKeys.includes(val.trim())
                  );
                  newDependentValue = validValues.join(';');
                  console.log(`  Current values: ${currentValues}`);
                  console.log(`  Valid values: ${validValues}`);
                  console.log(`  New value: ${newDependentValue}`);
                } else {
                  // This is a single picklist - clear if not valid
                  if (!availableKeys.includes(currentDependentValue.trim())) {
                    newDependentValue = '';
                    console.log(
                      `  Single value ${currentDependentValue} is not valid, clearing`
                    );
                  } else {
                    console.log(
                      `  Single value ${currentDependentValue} is still valid, keeping`
                    );
                  }
                }
              }

              return {
                ...prev,
                [dependentFieldName]: newDependentValue,
              };
            });
          } catch (error) {
            console.error(
              `Error getting dependent picklist values for ${dependentFieldName}:`,
              error
            );
            // Set empty array for dependent field on error
            setDependentPicklistValues(prev => ({
              ...prev,
              [dependentFieldName]: [],
            }));

            // Clear the dependent field value on error
            setFormData(prev => ({
              ...prev,
              [dependentFieldName]: '',
            }));
          }

          // Clear any errors for the dependent field
          if (errors[dependentFieldName]) {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[dependentFieldName];
              return newErrors;
            });
          }

          // Clear the label data for the dependent field if it's a lookup
          if (labelData[dependentFieldName]) {
            setLabelData(prev => {
              const newLabels = { ...prev };
              delete newLabels[dependentFieldName];
              return newLabels;
            });
          }
        });
      }
    }
  };

  const handleLookupChange = (fieldName, id, label) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: id,
    }));
    setLabelData(prev => ({
      ...prev,
      [fieldName]: label,
    }));

    // Update cache for related_record_id when user selects a new value
    if (
      objectName === 'task' &&
      fieldName === 'related_record_id' &&
      formData.related_object
    ) {
      console.log(
        'Updating cache for related_record_id:',
        formData.related_object,
        '->',
        id
      );
      setRelatedRecordCache(prevCache => ({
        ...prevCache,
        [formData.related_object]: {
          value: id,
          label: label,
        },
      }));
    }

    // Clear error for this field when user selects a lookup value
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Helper function to get dependent picklist values based on controlling field value
  const getDependentPicklistValues = async (
    controllingFieldName,
    controllingValue,
    dependentFieldName
  ) => {
    if (!picklistDependencies || picklistDependencies.length === 0) {
      console.log('No picklist dependencies found');
      return [];
    }

    // Find the dependency that matches our fields
    const dependency = picklistDependencies.find(
      dep =>
        dep.controlling_field_details?.name === controllingFieldName &&
        dep.dependent_field_details?.name === dependentFieldName
    );

    if (!dependency) {
      console.log(
        `No dependency found for ${controllingFieldName} → ${dependentFieldName}`
      );
      console.log(
        'Available dependencies:',
        picklistDependencies.map(d => ({
          controlling: d.controlling_field_details?.name,
          dependent: d.dependent_field_details?.name,
        }))
      );
      return [];
    }

    console.log('Found dependency:', dependency);
    console.log('Value dependencies:', dependency.value_dependencies);
    console.log(
      'Value dependencies length:',
      dependency.value_dependencies?.length || 0
    );

    // Check if value_dependencies exists and has data
    if (
      !dependency.value_dependencies ||
      dependency.value_dependencies.length === 0
    ) {
      console.log('No value dependencies found in dependency object');
      console.log('This means no value dependencies are configured yet');
      return [];
    }

    // Filter value dependencies based on the controlling value key
    // Handle both single picklist and multipicklist controlling values
    let dependentValueKeys = [];

    if (controllingValue.includes(';')) {
      // This is a multipicklist value (semicolon-separated)
      const controllingValues = controllingValue
        .split(';')
        .filter(v => v.trim() !== '');
      console.log(`Multipicklist controlling values:`, controllingValues);

      // Get dependent values for ALL selected controlling values
      const allDependentKeys = new Set(); // Use Set to avoid duplicates

      controllingValues.forEach(controllingVal => {
        const matchingDependencies = dependency.value_dependencies.filter(
          vd => vd.controlling_value_key === controllingVal.trim()
        );

        matchingDependencies.forEach(vd => {
          allDependentKeys.add(vd.dependent_value_key);
        });
      });

      dependentValueKeys = Array.from(allDependentKeys);
    } else {
      // This is a single picklist value
      dependentValueKeys = dependency.value_dependencies
        .filter(vd => vd.controlling_value_key === controllingValue)
        .map(vd => vd.dependent_value_key);
    }

    console.log(
      `Dependent value keys for ${controllingFieldName}=${controllingValue} → ${dependentFieldName}:`,
      dependentValueKeys
    );

    if (dependentValueKeys.length === 0) {
      console.log(
        `No dependent values found for controlling value: ${controllingValue}`
      );
      return [];
    }

    console.log('$$ picklistValues: ', picklistValues);
    // Get the dependent field picklist values from the current state
    const dependentPicklistValues = picklistValues[dependentFieldName] || [];
    console.log(
      'Available dependent picklist values:',
      dependentPicklistValues
    );

    // Filter the dependent picklist values based on the keys
    const filteredPicklistValues = dependentPicklistValues.filter(pv => {
      const pvKey = pv.key || pv.value_key;
      const isIncluded = dependentValueKeys.includes(pvKey);
      console.log(`Checking ${pvKey}: ${isIncluded ? 'INCLUDED' : 'excluded'}`);
      return isIncluded;
    });

    console.log(
      `Filtered picklist values for ${dependentFieldName}:`,
      filteredPicklistValues
    );
    return filteredPicklistValues;
  };

  // Helper function to check if a field has dependencies
  const hasFieldDependencies = fieldName => {
    if (!picklistDependencies || picklistDependencies.length === 0) {
      return false;
    }

    return picklistDependencies.some(
      dep =>
        dep.controlling_field_details?.name === fieldName ||
        dep.dependent_field_details?.name === fieldName
    );
  };

  // Helper function to check if a field is a dependent field
  const isDependentField = fieldName => {
    console.log(
      `@@ picklistDependencies.length: `,
      picklistDependencies.length
    );
    if (!picklistDependencies || picklistDependencies.length === 0) {
      console.log(
        `@@ isDependentField(${fieldName}): No dependencies loaded yet, returning false`
      );
      return false;
    }

    console.log(
      `@@ isDependentField(${fieldName}): Checking dependencies:`,
      picklistDependencies
    );
    console.log(
      `@@ isDependentField(${fieldName}): Available fields in dependencies:`,
      picklistDependencies.map(dep => ({
        controlling: dep.controlling_field_details?.name,
        dependent: dep.dependent_field_details?.name,
        controlling_obj: dep.controlling_field_details,
        dependent_obj: dep.dependent_field_details,
      }))
    );

    const isDependent = picklistDependencies.some(
      dep => dep.dependent_field_details?.name === fieldName
    );

    console.log(
      `@@ isDependentField(${fieldName}): ${isDependent ? 'YES' : 'NO'}`
    );
    return isDependent;
  };

  // Helper function to check if a field is a controlling field
  const isControllingField = fieldName => {
    if (!picklistDependencies || picklistDependencies.length === 0) {
      console.log(
        `isControllingField(${fieldName}): No dependencies loaded yet, returning false`
      );
      return false;
    }

    const isControlling = picklistDependencies.some(
      dep => dep.controlling_field_details?.name === fieldName
    );

    console.log(
      `isControllingField(${fieldName}): ${isControlling ? 'YES' : 'NO'}`
    );
    return isControlling;
  };

  // Helper function to get the controlling field for a dependent field
  const getControllingFieldForDependent = dependentFieldName => {
    if (!picklistDependencies || picklistDependencies.length === 0) {
      return null;
    }

    const dependency = picklistDependencies.find(
      dep => dep.dependent_field_details?.name === dependentFieldName
    );

    return dependency ? dependency.controlling_field_details?.name : null;
  };

  // Helper function to get dependency information for tooltips
  const getDependencyInfo = fieldName => {
    if (!picklistDependencies || picklistDependencies.length === 0) {
      return null;
    }

    // Check if this field is controlling other fields
    const controllingDependencies = picklistDependencies.filter(
      dep => dep.controlling_field_details?.name === fieldName
    );

    // Check if this field is dependent on other fields
    const dependentDependencies = picklistDependencies.filter(
      dep => dep.dependent_field_details?.name === fieldName
    );

    const controllingFields = controllingDependencies
      .map(dep => {
        const fieldName = dep.dependent_field_details?.name;
        const fieldMeta = fieldMetaByName[fieldName];
        return fieldMeta?.label || fieldName;
      })
      .filter(Boolean);

    const dependentFields = dependentDependencies
      .map(dep => {
        const fieldName = dep.controlling_field_details?.name;
        const fieldMeta = fieldMetaByName[fieldName];
        return fieldMeta?.label || fieldName;
      })
      .filter(Boolean);

    // Build concise tooltip message
    let tooltipParts = [];

    if (dependentFields.length > 0) {
      tooltipParts.push(`Depends: ${dependentFields.join(', ')}`);
    }

    if (controllingFields.length > 0) {
      tooltipParts.push(`Controls: ${controllingFields.join(', ')}`);
    }

    if (tooltipParts.length > 0) {
      return {
        hasDependencies: true,
        tooltip: tooltipParts.join('\n'),
        isControlling: controllingFields.length > 0,
        isDependent: dependentFields.length > 0,
      };
    }

    return null;
  };

  // Helper function to get available picklist values for a dependent field
  const getAvailablePicklistValuesForDependent = async fieldName => {
    if (!isDependentField(fieldName)) {
      return picklistValues[fieldName] || [];
    }

    const controllingFieldName = getControllingFieldForDependent(fieldName);
    if (!controllingFieldName) {
      return [];
    }

    const controllingValue = formData[controllingFieldName];
    if (!controllingValue || controllingValue === '') {
      return []; // No controlling value selected, so no dependent values available
    }

    try {
      return await getDependentPicklistValues(
        controllingFieldName,
        controllingValue,
        fieldName
      );
    } catch (error) {
      console.error('Error getting dependent picklist values:', error);
      return [];
    }
  };

  // Helper function to check if a dependent field should be disabled
  const shouldDisableDependentField = fieldName => {
    if (!isDependentField(fieldName)) {
      return false;
    }

    const controllingFieldName = getControllingFieldForDependent(fieldName);
    if (!controllingFieldName) {
      return true; // No controlling field found, disable
    }

    const controllingValue = formData[controllingFieldName];
    if (!controllingValue || controllingValue === '') {
      return true; // No controlling value selected, disable
    }

    // Check if the dependent field has any available values
    const dependentValues = dependentPicklistValues[fieldName];

    // If dependentValues is undefined, it means the field hasn't been initialized yet
    if (dependentValues === undefined) {
      return true; // Not initialized yet, disable
    }

    // If dependentValues is an empty array or has no values, disable
    if (!dependentValues || dependentValues.length === 0) {
      return true; // No dependent values available, disable
    }

    return false; // Has values, enable
  };

  const handleCancel = () => {
    // Check if there are unsaved changes
    const hasUnsavedChanges = Object.keys(formData).some(key => {
      const originalValue = recordData?.details
        ?.flatMap(s => s.fields)
        ?.find(f => f.fieldName === key)?.value;
      return formData[key] !== originalValue;
    });

    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!confirmed) {
        return;
      }
    }

    if (onCancel) {
      // If onCancel prop is provided, use it
      onCancel();
    } else {
      // Smart navigation logic based on source and record type
      let targetUrl;

      if (!id) {
        // New record - always go to list view
        targetUrl = `/listview/${objectName}`;
      } else {
        // Existing record - check source page
        if (sourcePage === 'detail' || sourcePage === 'detailview') {
          // Came from detail view - go back to detail view
          targetUrl = `/view/${objectName}/${id}`;
        } else {
          // Came from list view or unknown source - go to list view
          targetUrl = `/listview/${objectName}`;
        }
      }

      window.location.href = targetUrl;
    }
  };

  // Helper function to show toast
  const showToast = (message, variant = 'success') => {
    setToast({
      show: true,
      message,
      variant,
    });
  };

  // Function to close toast
  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // Function to focus on the first field with an error and scroll to top
  const focusFirstErrorField = errorFields => {
    if (Object.keys(errorFields).length > 0) {
      // Scroll to top of the page first
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Then focus on the first error field
      const firstErrorFieldName = Object.keys(errorFields)[0];
      const errorFieldElement = document.querySelector(
        `[name="${firstErrorFieldName}"]`
      );
      if (errorFieldElement) {
        // Small delay to ensure scroll completes before focus
        setTimeout(() => {
          errorFieldElement.focus();
        }, 500);
      }
    }
  };

  const handleSubmit = async (e, saveAndNew = false) => {
    e.preventDefault();

    // Prevent double submission
    if (isSaving) {
      console.log('Save already in progress, ignoring duplicate submission');
      return;
    }

    // Set saving state to disable form
    setIsSaving(true);

    // Set flag to show errors after submission attempt
    setHasSubmitted(true);

    console.log('Form submission started');
    console.log('Current formData:', formData);
    console.log('Record ID:', id);
    console.log('Object name:', objectName);
    console.log('Save and New:', saveAndNew);

    const newErrors = {};
    const detailFields =
      recordData?.details?.flatMap(s => s.fields || []) || [];
    const hiddenSubmit = Array.isArray(recordData?.otherHiddenFields)
      ? recordData.otherHiddenFields
      : [];
    const fieldByName = new Map();
    hiddenSubmit.forEach(f => {
      if (f?.fieldName && !fieldByName.has(f.fieldName)) {
        fieldByName.set(f.fieldName, f);
      }
    });
    detailFields.forEach(f => {
      if (f?.fieldName) fieldByName.set(f.fieldName, f);
    });
    const allFields = Array.from(fieldByName.values());

    console.log('All fields:', allFields);

    allFields.forEach(field => {
      // Skip validation for system fields
      const systemFields = [
        'createddate',
        'lastmodifieddate',
        'createdbyid',
        'lastmodifiedbyid',
      ];
      if (systemFields.includes(field.fieldName?.toLowerCase())) {
        return; // Skip validation for system fields
      }
      if (
        !id &&
        !cloneFromId &&
        field.fieldName?.toLowerCase() === 'id'
      ) {
        return;
      }

      const error = validateField(field);
      if (error) {
        newErrors[field.fieldName] = error;
      }
    });

    // Validate attendees for all task types
    if (objectName === 'task' && attendees.length > 0) {
      const attendeeErrors = [];
      attendees.forEach((attendee, index) => {
        // At least one of: userid, email, or mobile must be provided
        const hasUserid = attendee.userid && attendee.userid.trim();
        const hasEmail = attendee.email && attendee.email.trim();
        const hasMobile = attendee.mobile && attendee.mobile.trim();

        if (!hasUserid && !hasEmail && !hasMobile) {
          attendeeErrors.push(`Attendee ${index + 1}: At least one of User, Email, or Mobile is required`);
        }

        // If email is provided, validate format
        if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendee.email)) {
          attendeeErrors.push(`Attendee ${index + 1}: Invalid email format`);
        }
      });

      if (attendeeErrors.length > 0) {
        newErrors['attendees'] = attendeeErrors.join(', ');
      }
    }

    console.log('newErrors', newErrors);

    // Only set errors if there are actual validation errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      // Clear all errors if validation passes
      setErrors({});
    }

    if (Object.keys(newErrors).length > 0) {
      // Reset saving state on validation error
      setIsSaving(false);
      // Show error toast
      showToast('Please fix the validation errors on the page!', 'danger');
      // Focus on the first field with an error
      focusFirstErrorField(newErrors);
      return;
    }

    try {
      // Check license capacity by role (per license_type). Skip when inactivating the user so the record is always saved.
      const isInactivatingUser = objectName === 'user' && id && (formData.isactive === false || formData.isactive === 'false');
      if (objectName === 'user' && !isInactivatingUser) {
        const roleId = formData.role_id || formData.roleid || null;
        if (roleId) {
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const excludeParam = id ? `?excludeUserId=${encodeURIComponent(id)}` : '';
              const response = await fetch(
                `${constants.API_BASE_URL}/api/auth/licenses-with-usage${excludeParam}`,
                { headers: { Authorization: token } }
              );
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.roleToLicenseTypeMap && data.licenses) {
                  const licenseTypeId = data.roleToLicenseTypeMap[roleId];
                  if (licenseTypeId) {
                    const license = data.licenses.find((l) => l.id === licenseTypeId || l.license_type_id === licenseTypeId);
                    if (license && license.available <= 0) {
                      setIsSaving(false);
                      showToast(
                        `No available seat for "${license.license_type_name}". Used ${license.used_count} of ${license.quantity}.`,
                        'danger'
                      );
                      return;
                    }
                  }
                }
              }
            }
          } catch (licenseError) {
            console.error('Error checking license capacity:', licenseError);
            // Don't block save if check fails – backend will enforce
          }
        }
      }

      // Sanitize the data before sending to API
      const sanitizedData = {};
      allFields.forEach(field => {
        const value = formData[field.fieldName];
        console.log('field.fieldName', field.fieldName);
        // Skip system fields but allow readonly fields that might have values
        if (field.fieldName === 'id') {
          return;
        }

        // Skip autonumber fields as they are auto-generated
        if (field.type === 'autonumber') {
          return;
        }

        // Skip calculated fields as they are computed at runtime
        if (field.type === 'calculated') {
          return;
        }

        // Skip system fields as they are managed by the system
        const systemFields = [
          'createddate',
          'lastmodifieddate',
          'createdbyid',
          'lastmodifiedbyid',
        ];
        if (systemFields.includes(field.fieldName?.toLowerCase())) {
          return;
        }

        if (fieldMetaByName[field.fieldName]?.flsNoEdit) {
          return;
        }

        // Handle different field types
        if (field.type === 'datetime' && value) {
          // Convert datetime-local to ISO string with user's timezone
          // The datetime-local value is already in the user's local timezone
          // We need to preserve it as-is in the user's configured timezone
          const prefs = getUserPreferences();
          const userTimezone = prefs.time_zone || 'UTC';

          // Parse the datetime-local value (format: YYYY-MM-DDTHH:mm)
          // and interpret it in the user's timezone
          const momentDate = moment.tz(value, userTimezone);

          // Format as ISO string with timezone offset (e.g., 2025-11-25T17:30:00+05:30)
          sanitizedData[field.fieldName] = momentDate.format();
        } else if (field.type === 'date' && value) {
          // For date fields, we still want to preserve the date in user's timezone
          // but set time to start of day (00:00:00) in user's timezone
          const prefs = getUserPreferences();
          const userTimezone = prefs.time_zone || 'UTC';

          // Parse the date value (format: YYYY-MM-DD) and set to start of day in user's timezone
          const momentDate = moment.tz(value, userTimezone).startOf('day');

          // Format as ISO string with timezone offset
          sanitizedData[field.fieldName] = momentDate.format();
        } else if (field.type === 'time' && value) {
          // Convert time to HH:MM:SS format (add seconds if not present)
          let timeValue = value;
          if (timeValue && timeValue.includes(':')) {
            const parts = timeValue.split(':');
            if (parts.length === 2) {
              // If only HH:MM, add :00 for seconds
              timeValue = timeValue + ':00';
            }
          }
          sanitizedData[field.fieldName] = timeValue;
        } else if (field.type === 'number' && value !== '') {
          // Convert to number
          sanitizedData[field.fieldName] = Number(value);
        } else if (field.type === 'boolean') {
          // Convert to boolean
          sanitizedData[field.fieldName] = Boolean(value);
        } else if (field.type === 'password') {
          // For password fields: send value only if user entered something (empty = keep existing)
          sanitizedData[field.fieldName] = value || '';
          console.log(`Password field ${field.fieldName}: sending value='${value ? '[REDACTED]' : '(empty - keep existing)'}'`);
        } else {
          // For text, email, phone, picklist, lookup, etc.
          sanitizedData[field.fieldName] = value || null;
        }
      });

      // Handle parent record field from queryParams if coming from ResizableTable / activity widget
      if (queryParams && queryParams.targetField && queryParams.targetId) {
        const parentFieldName = queryParams.targetField;
        const parentRecordId = queryParams.targetId;

        const existingParent = sanitizedData[parentFieldName];
        const parentMissingOrEmpty =
          !Object.prototype.hasOwnProperty.call(sanitizedData, parentFieldName) ||
          existingParent === null ||
          existingParent === undefined ||
          existingParent === '';

        if (parentMissingOrEmpty) {
          console.log(
            `Setting parent field ${parentFieldName} from queryParams to ${parentRecordId}`
          );
          sanitizedData[parentFieldName] = parentRecordId;
        } else {
          console.log(
            `Parent field ${parentFieldName} already set in form:`,
            sanitizedData[parentFieldName]
          );
        }

        // For notes, also set related_object if parentObjectName is provided
        // This ensures activities created from notes have the correct related_object
        if (objectName === 'note' && queryParams.parentObjectName) {
          const ro = sanitizedData.related_object;
          const relatedObjectMissingOrEmpty =
            !Object.prototype.hasOwnProperty.call(sanitizedData, 'related_object') ||
            ro === null ||
            ro === undefined ||
            ro === '';
          if (relatedObjectMissingOrEmpty) {
            console.log(
              `Adding related_object ${queryParams.parentObjectName} to note for activity sync`
            );
            sanitizedData.related_object = queryParams.parentObjectName;
          }
        }
      }

      // Add attendees to payload for all task types
      if (objectName === 'task' && attendees.length > 0) {
        // Only include attendees with at least one of: userid, email, or mobile
        const validAttendees = attendees.filter(
          att => {
            const hasUserid = att.userid && att.userid.trim();
            const hasEmail = att.email && att.email.trim();
            const hasMobile = att.mobile && att.mobile.trim();
            return hasUserid || hasEmail || hasMobile;
          }
        );
        if (validAttendees.length > 0) {
          sanitizedData.attendees = validAttendees;
          console.log('Adding attendees to payload:', validAttendees);
        }
      }

      // Add selectedWhatsAppSetting for tasks (from sessionStorage/localStorage)
      if (objectName === 'task') {
        const selectedWhatsAppSetting = sessionStorage.getItem('selectedWhatsAppSetting') || localStorage.getItem('selectedWhatsAppSetting') || null;
        if (selectedWhatsAppSetting) {
          sanitizedData.selectedWhatsAppSetting = selectedWhatsAppSetting;
          console.log('Adding selectedWhatsAppSetting to task payload:', selectedWhatsAppSetting);
        }
      }

      if (
        !id &&
        queryParams?.recordviewId &&
        String(queryParams.recordviewId).trim() !== ''
      ) {
        const rv = String(queryParams.recordviewId).trim();
        const objectHasRecordviewField = allFields.some(
          f =>
            f?.fieldName &&
            (String(f.fieldName).toLowerCase() === 'recordviewid' ||
              String(f.fieldName).toLowerCase() === 'recordview_id' ||
              (String(f.type || '').toLowerCase() === 'lookup' &&
                String(f.referenceObject || '').toLowerCase() === 'recordview'))
        );
        const hasRvInPayload = Object.prototype.hasOwnProperty.call(
          sanitizedData,
          'recordviewid'
        );
        const hasRvUnderscore = Object.prototype.hasOwnProperty.call(
          sanitizedData,
          'recordview_id'
        );
        if (objectHasRecordviewField && !hasRvInPayload && !hasRvUnderscore) {
          sanitizedData.recordviewid = rv;
        }
      }

      console.log('Sanitized data being sent to API:', sanitizedData);
      console.log('API method:', id ? 'update' : 'create');
      console.log('API endpoint:', `object/${objectName}`);

      // Single company currency: align payload with backend when multi_currency is off
      const hasCurrencyField = allFields.some(
        (f) =>
          f.type === 'currency' ||
          String(f.fieldName || '').toLowerCase() === 'currency'
      );
      if (hasCurrencyField) {
        try {
          const { multiCurrencyEnabled, companyCurrency } = await getMultiCurrencySettings();
          if (!multiCurrencyEnabled && companyCurrency) {
            sanitizedData.currency = companyCurrency;
            delete sanitizedData.exchange_rate;
          }
        } catch (currencySettingErr) {
          console.warn('EditLayout: could not apply company currency to payload', currencySettingErr?.message);
        }
      }

      const dataApi = new DataApi(`object/${objectName}`);
      const method = id ? 'update' : 'create';
      const response = await dataApi[method](sanitizedData, id);

      console.log('API Response:', response);
      console.log('Is new record creation:', !id);
      console.log('Response data structure:', response?.data);

      // Call onSave callback if provided

      console.log('$$ saveAndNew', saveAndNew);
      if (saveAndNew) {
        if (onSaveAndNew) {
          onSaveAndNew(response.data);
        }
      } else {
        if (onSave && response?.data) {
          onSave(response.data);
        }
      }

      setSubmitError(null);

      // Handle Save and New functionality
      if (saveAndNew) {
        const successMessage = id
          ? 'Record updated successfully!'
          : 'Record created successfully!';

        if (openedInModal) {
          // In modal context - clear form and reset for new record
          setFormData({});
          setErrors({});
          setLabelData({});
          setHasSubmitted(false);
          setSuccessMessage(successMessage);

          // Fetch fresh data for new record
          setTimeout(() => {
            fetchRecord();
          }, 100);
        } else {
          // In standalone context - clear form and redirect
          setFormData({});
          setErrors({});
          setLabelData({});
          setHasSubmitted(false);
          setSuccessMessage(successMessage);

          // Reset the ID to null to indicate new record
          // This will trigger the useEffect to fetch fresh data for new record
          window.history.replaceState(null, '', `/edit/${objectName}`);

          // Fetch fresh data for new record
          setTimeout(() => {
            fetchRecord();
          }, 100);
        }

        return;
      }

      // Smart navigation logic based on source and record type
      const recordId = response?.data?.id || response?.data?.data?.id || id;
      if (recordId) {
        const successMessage = id
          ? 'Record updated successfully!'
          : 'Record created successfully!';
        let targetUrl;

        // Related list modal: onSave already ran above — avoid double callback / double refresh
        if (openedInModal) {
          return;
        }

        if (!id) {
          // New record - always go to list view
          targetUrl = `/listview/${objectName}?success=${encodeURIComponent(successMessage)}`;
        } else {
          // Existing record - check source page
          if (sourcePage === 'detail' || sourcePage === 'detailview') {
            // Came from detail view - go back to detail view
            targetUrl = `/view/${objectName}/${recordId}?success=${encodeURIComponent(successMessage)}`;
          } else {
            // Came from list view or unknown source - go to list view
            targetUrl = `/listview/${objectName}?success=${encodeURIComponent(successMessage)}`;
          }
        }

        console.log(`Redirecting to: ${targetUrl}`);
        // Immediate redirect with success message in URL
        window.location.href = targetUrl;
        return; // Exit early to prevent further processing
      }

      // Notify parent window if in iframe
      if (window.parent !== window) {
        window.parent.postMessage(
          { type: 'saveSuccess', data: response?.data },
          '*'
        );
      }

      // Handle modal/iframe scenarios for redirect
      const modalRecordId =
        response?.data?.id || response?.data?.data?.id || id;
      if (window.parent !== window && modalRecordId) {
        const successMessage = id
          ? 'Record updated successfully!'
          : 'Record created successfully!';
        const viewUrl = `/view/${objectName}/${modalRecordId}?success=${encodeURIComponent(successMessage)}`;
        console.log(`Notifying parent window to redirect to: ${viewUrl}`);
        window.parent.postMessage(
          {
            type: 'redirectToView',
            url: viewUrl,
            objectName: objectName,
            recordId: modalRecordId,
            successMessage: successMessage,
          },
          '*'
        );
      }
    } catch (error) {
      console.error('Save error:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save record. Please try again.';

      // Show error toast notification
      showToast(message, 'danger');

      // Also set error state for the error banner (if needed)
      setSubmitError(message);

      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      // Always reset saving state when operation completes
      setIsSaving(false);
    }
  };

  const handleSaveAndNew = e => {
    handleSubmit(e, true);
  };

  // Attendee management functions
  const addAttendee = () => {
    setAttendees([...attendees, { name: '', email: '', mobile: '', userid: null, userLabel: '', id: null }]);
  };

  const removeAttendee = (index) => {
    const updatedAttendees = attendees.filter((_, i) => i !== index);
    setAttendees(updatedAttendees);
  };

  const updateAttendee = (index, field, value) => {
    const updatedAttendees = [...attendees];
    updatedAttendees[index][field] = value;
    setAttendees(updatedAttendees);
  };

  // Handle user lookup change for attendees
  const handleAttendeeUserChange = (index, userId, userLabel) => {
    const updatedAttendees = [...attendees];
    updatedAttendees[index].userid = userId;
    updatedAttendees[index].userLabel = userLabel;
    // If user is selected, optionally populate name and email from user data
    // (This can be enhanced later to fetch user details)
    setAttendees(updatedAttendees);
  };

  // Listen for messages from parent window and custom events
  useEffect(() => {
    const handleMessage = event => {
      if (event.data.action === 'save') {
        console.log('$$ handleMessage save');
        handleSubmit(new Event('submit'));
      } else if (event.data.action === 'saveAndNew') {
        console.log('$$ handleMessage saveAndNew');
        // Handle save and new logic
        handleSubmit(new Event('submit')).then(() => {
          // Clear form and prepare for new record
          setFormData({});
          setErrors({});
        });
      }
    };

    const handleCustomSubmit = event => {
      console.log('$$ handleCustomSubmit');
      if (event.detail && event.detail.saveAndNew) {
        // Prevent default form submission to avoid double submission
        event.preventDefault();
        event.stopPropagation();
        // Handle Save and New from modal
        handleSubmit(event, true);
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('submit', handleCustomSubmit, true);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('submit', handleCustomSubmit, true);
    };
  }, [formData, recordData, errors, id, objectName, onSave]);

  // Listen for keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = event => {
      // Escape key to cancel
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
      // Ctrl+S or Cmd+S to save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleSubmit(new Event('submit'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData, recordData, errors, id, objectName, onSave]);

  const handleBlur = field => {
    // Skip validation for system fields
    const systemFields = [
      'createddate',
      'lastmodifieddate',
      'createdbyid',
      'lastmodifiedbyid',
    ];
    if (systemFields.includes(field.fieldName?.toLowerCase())) {
      return; // Skip validation for system fields
    }
    if (
      !id &&
      !cloneFromId &&
      field.fieldName?.toLowerCase() === 'id'
    ) {
      return;
    }

    const error = validateField(field);

    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field.fieldName] = error;
      } else {
        // Remove the error entry if validation passes
        delete newErrors[field.fieldName];
      }
      return newErrors;
    });
  };

  // Enhanced error display function
  const getFieldError = fieldName => {
    // Show errors if form has been submitted or if there's a specific error for this field
    // This allows for both form-level and field-level error display
    return hasSubmitted || errors[fieldName] ? errors[fieldName] : '';
  };

  // Enhanced field validation styling
  const getFieldValidationClass = fieldName => {
    const hasError = getFieldError(fieldName);

    // Debug: Log validation class for specific fields
    if (
      fieldName === 'gender' ||
      fieldName === 'age' ||
      fieldName === 'firstname'
    ) {
      console.log('�� getFieldValidationClass for:', fieldName, {
        hasError: hasError,
        errorMessage: errors[fieldName],
        hasSubmitted: hasSubmitted,
        validationClass: hasError ? 'is-invalid' : '',
      });
    }

    return hasError ? 'is-invalid' : '';
  };

  // Helper function to check if there are any remaining errors
  const hasRemainingErrors = () => {
    return Object.keys(errors).length > 0;
  };

  // Auto-hide error summary when all errors are resolved
  useEffect(() => {
    if (hasSubmitted && !hasRemainingErrors()) {
      setHasSubmitted(false);
    }
  }, [errors, hasSubmitted]);

  // Helper: get allow_negative from field display_config (number/currency validation)
  const getAllowNegative = (field) => {
    if (!field) return true;
    const dc = field.displayConfig;
    if (dc == null || dc === undefined) return true;
    const config = typeof dc === 'string' ? (() => { try { return JSON.parse(dc); } catch { return {}; } })() : (dc || {});
    return config.allow_negative !== false;
  };

  // Helper function to validate phone number
  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return true; // Allow empty if not required

    // Remove all non-digit characters for validation
    const digitsOnly = phoneNumber.replace(/\D/g, '');

    // Check if phone number contains at least some digits
    if (digitsOnly.length === 0) {
      return false; // No digits found - invalid
    }

    // Check if phone number has reasonable length (minimum 10 digits for most countries)
    if (digitsOnly.length < 10) {
      return false;
    }

    // Check if phone number has too many digits (maximum 15 for international)
    if (digitsOnly.length > 15) {
      return false;
    }

    // Allow formats like: +1-234-567-8900, (123) 456-7890, 123-456-7890, 1234567890, etc.
    const phonePattern = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,5}[-\s\.]?[0-9]{1,4}$/;
    return phonePattern.test(phoneNumber);
  };

  // Function to validate a single field
  const validateField = field => {
    if (
      !id &&
      !cloneFromId &&
      field.fieldName?.toLowerCase() === 'id'
    ) {
      return '';
    }

    const value = formData[field.fieldName] || '';
    let error = '';

    // Debug: Log validation for specific fields
    if (
      field.fieldName === 'age' ||
      field.fieldName === 'firstname' ||
      field.fieldName === 'lastname' ||
      field.fieldName === 'annual_revenue' ||
      field.fieldName === 'employees'
    ) {
      console.log('🔍 Validating field:', field.fieldName, {
        value: value,
        isRequired: field.isRequired,
        type: field.type,
        precision: field.precision,
        scale: field.scale,
        fieldData: field,
      });
    }

    let effectiveRequired =
      objectName === 'mail_config'
        ? isMailConfigFieldRequired(field.fieldName, formData?.type || formData?.Type, field.isRequired)
        : objectName === 'task'
          ? isFieldRequired(field.fieldName, formData?.task_type || formData?.tasktype || formData?.type, field.isRequired)
          : field.isRequired;

    // Password fields: not required when editing (leave empty to keep existing)
    if (field.type === 'password' && id && !cloneFromId) {
      effectiveRequired = false;
    }

    const fieldTypeLc = (field.type || '').toLowerCase();
    if (fieldTypeLc === 'autonumber' || fieldTypeLc === 'calculated') {
      effectiveRequired = false;
    }
    if (field.isReadonly) {
      effectiveRequired = false;
    }

    if (effectiveRequired && !value) {
      error = `${field.label} is required.`;
      console.log('🔍 Field validation error:', field.fieldName, error);
    } else if (field.type === 'email' && value && !Validator.isEmail(value)) {
      error = 'Invalid email format.';
    } else if (field.type === 'url' && value && !Validator.isURL(value)) {
      error = 'Invalid URL.';
    } else if (field.type === 'phone' && value && !validatePhoneNumber(value)) {
      error = 'Invalid phone number. Please enter a valid phone number with at least 10 digits.';
    } else if (
      ['number', 'decimal', 'float', 'currency'].includes(
        field.type?.toLowerCase()
      ) &&
      value
    ) {
      // Validate numeric fields with precision constraints
      const numValue = Number(value);
      if (isNaN(numValue)) {
        error = 'Invalid numeric value.';
      } else {
        // Check allow_negative from display_config (object_config_fields)
        const allowNegative = getAllowNegative(field);
        if (!allowNegative && numValue < 0) {
          error = `${field.label} does not allow negative values.`;
        } else if (field.precision) {
          const validation = Validator.validateNumericPrecision(
            numValue,
            field.precision,
            field.scale || 0
          );
          if (!validation.isValid) {
            error = validation.message;
          }
        }
      }
    }

    return error;
  };

  // Helper function to extract lookup_filter and lookup_filter_logic from field data
  const extractLookupFilter = (field) => {
    let lookupFilterArray = null;
    let lookupFilterLogic = 'AND';
    console.log("field.lookupFilter: ", field.lookupFilter);
    console.log("field.lookup_filter: ", field.lookup_filter);
    console.log("Full field object: ", field);
    if (field.lookupFilter || field.lookup_filter) {
      const lookupFilter = field.lookupFilter || field.lookup_filter;

      if (typeof lookupFilter === 'string') {
        try {
          const parsed = JSON.parse(lookupFilter);
          if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed.filters) {
            lookupFilterArray = Array.isArray(parsed.filters) ? parsed.filters : null;
            lookupFilterLogic = parsed.logic && (parsed.logic.toUpperCase() === 'OR' || parsed.logic.toUpperCase() === 'AND')
              ? parsed.logic.toUpperCase()
              : 'AND';
          } else if (Array.isArray(parsed)) {
            lookupFilterArray = parsed;
            lookupFilterLogic = 'AND';
          }
        } catch (e) {
          console.error('Failed to parse lookup_filter:', e);
        }
      } else if (typeof lookupFilter === 'object' && !Array.isArray(lookupFilter) && lookupFilter.filters) {
        lookupFilterArray = Array.isArray(lookupFilter.filters) ? lookupFilter.filters : null;
        lookupFilterLogic = lookupFilter.logic && (lookupFilter.logic.toUpperCase() === 'OR' || lookupFilter.logic.toUpperCase() === 'AND')
          ? lookupFilter.logic.toUpperCase()
          : 'AND';
      } else if (Array.isArray(lookupFilter)) {
        lookupFilterArray = lookupFilter;
        lookupFilterLogic = 'AND';
      }
    }

    return { lookupFilterArray, lookupFilterLogic };
  };

  /** Pass into LookupLayout so recordview searches are limited to the object for this form (API name). */
  const scopeRecordviewToObjectNameForField = field => {
    const ref = String(field.referenceObject || field.reference_object || '')
      .toLowerCase()
      .trim();
    const fn = String(field.fieldName || field.field_name || '').toLowerCase();
    const isRecordviewLookup =
      ref === 'recordview' ||
      fn === 'recordviewid' ||
      fn === 'recordview_id';
    if (!isRecordviewLookup || !objectName || !String(objectName).trim()) {
      return undefined;
    }
    return String(objectName).trim();
  };

  const recordviewAllowlistForLookupField = (field) => {
    const scope = scopeRecordviewToObjectNameForField(field);
    if (
      !scope ||
      String(scope).toLowerCase().trim() !== String(objectName || '').toLowerCase().trim()
    ) {
      return null;
    }
    return roleRecordviewIdAllowlist;
  };

  const renderInputField = field => {
    const value = formData[field.fieldName] || '';
    const fieldError = getFieldError(field.fieldName);
    const validationClass = getFieldValidationClass(field.fieldName);

    console.log(
      'Rendering field:',
      field.fieldName,
      'with type:',
      field.type,
      'and value:',
      value
    );
    console.log('Object name:', objectName);
    console.log('Full field object:', field);

    // Special handling for task object fields
    if (objectName === 'task') {
      // Handle related_object field - display as dropdown
      if (field.fieldName?.toLowerCase() === 'related_object') {
        return (
          <>
            <Form.Select
              name={field.fieldName}
              value={value || ''}
              onChange={e => {
                handleChange(e, field.fieldName);
                // Clear error for this field when user makes a selection
                if (errors[field.fieldName]) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            >
              <option value=''>-- Select Related Object --</option>
              {availableObjects.map(obj => (
                <option key={obj.object_name} value={obj.object_name}>
                  {obj.object_label || obj.object_name}
                </option>
              ))}
            </Form.Select>
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );
      }

      // Handle related_object_email_field - show only when checkbox is checked and related_object is selected
      if (field.fieldName?.toLowerCase() === 'related_object_email_field') {
        const shouldShow = formData.send_notification_to_related_record && formData.related_object;

        if (!shouldShow) {
          return null; // Don't render the field if conditions aren't met
        }

        return (
          <>
            <Form.Select
              name={field.fieldName}
              value={value || ''}
              onChange={e => {
                handleChange(e, field.fieldName);
                // Clear error for this field when user makes a selection
                if (errors[field.fieldName]) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving || relatedObjectEmailFields.length === 0}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            >
              <option value=''>-- Select Email Field --</option>
              {relatedObjectEmailFields.map(emailField => (
                <option key={emailField.id} value={emailField.field_name}>
                  {emailField.field_label || emailField.field_name}
                </option>
              ))}
            </Form.Select>
            {relatedObjectEmailFields.length === 0 && formData.related_object && (
              <small className='text-muted'>
                No email fields found in {formData.related_object}
              </small>
            )}
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );
      }

      // Handle related_object_phone_field - show only when checkbox is checked and related_object is selected
      if (field.fieldName?.toLowerCase() === 'related_object_phone_field') {
        const shouldShow = formData.send_notification_to_related_record && formData.related_object;

        if (!shouldShow) {
          return null; // Don't render the field if conditions aren't met
        }

        return (
          <>
            <Form.Select
              name={field.fieldName}
              value={value || ''}
              onChange={e => {
                handleChange(e, field.fieldName);
                // Clear error for this field when user makes a selection
                if (errors[field.fieldName]) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving || relatedObjectPhoneFields.length === 0}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            >
              <option value=''>-- Select Phone Field --</option>
              {relatedObjectPhoneFields.map(phoneField => (
                <option key={phoneField.id} value={phoneField.field_name}>
                  {phoneField.field_label || phoneField.field_name}
                </option>
              ))}
            </Form.Select>
            {relatedObjectPhoneFields.length === 0 && formData.related_object && (
              <small className='text-muted'>
                No phone fields found in {formData.related_object}
              </small>
            )}
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );
      }
    }

    console.log(
      'Entering switch statement with field type:',
      field.type?.toLowerCase()
    );
    switch (field.type?.toLowerCase()) {
      case 'picklist':
        // Check if this is a dependent field
        const isDependent = isDependentField(field.fieldName);
        const isControlling = isControllingField(field.fieldName);

        // Check if currency field should be disabled (if opportunity/quote has products)
        const isCurrencyPicklistField = field.fieldName?.toLowerCase() === 'currency';
        const shouldDisableCurrencyPicklist = isCurrencyPicklistField &&
          (objectName === 'opportunity' || objectName === 'quote') &&
          hasProducts;

        // Get appropriate picklist values based on dependency status
        let fieldPicklistValues;
        if (isDependent) {
          // For dependent fields, show filtered values if available, otherwise show NO values
          const filteredValues = dependentPicklistValues[field.fieldName];
          if (filteredValues !== undefined) {
            // Check if it's been initialized
            if (filteredValues && filteredValues.length > 0) {
              fieldPicklistValues = filteredValues;
              console.log(
                `Dependent field ${field.fieldName}: Using filtered values:`,
                fieldPicklistValues
              );
            } else {
              // No filtered values available, show empty array
              fieldPicklistValues = [];
              console.log(
                `Dependent field ${field.fieldName}: No filtered values available, showing empty array`
              );
            }
          } else {
            // If dependentPicklistValues is undefined, it means no controlling value is selected yet.
            // In this case, we should show NO values for the dependent field.
            fieldPicklistValues = [];
            console.log(
              `Dependent field ${field.fieldName}: No controlling value selected, showing NO values.`
            );
          }
          console.log(
            `Dependent field ${field.fieldName}: Final values structure:`,
            fieldPicklistValues.map(v => ({
              key: v.key || v.value_key,
              value: v.value || v.value_label,
            }))
          );
        } else {
          fieldPicklistValues = picklistValues[field.fieldName] || [];
        }

        // Task notification_type: hide WhatsApp option when WatConnect package is not enabled
        if (objectName === 'task' && field.fieldName?.toLowerCase() === 'notification_type' && !hasPackage('watconnect')) {
          fieldPicklistValues = (fieldPicklistValues || []).filter(
            opt => (opt.key || opt.value_key || '').toLowerCase() !== 'whatsapp'
          );
        }

        // Determine if field should be disabled
        const shouldDisable =
          field.isReadonly ||
          (isDependent && shouldDisableDependentField(field.fieldName)) ||
          shouldDisableCurrencyPicklist;

        // Debug: Log field disable logic for dependent fields
        if (isDependent) {
          console.log(`🔍 Dependent field ${field.fieldName} disable logic:`, {
            isReadonly: field.isReadonly,
            isDependent,
            shouldDisableDependentField: shouldDisableDependentField(
              field.fieldName
            ),
            shouldDisable,
            controllingField: getControllingFieldForDependent(field.fieldName),
            controllingValue:
              formData[getControllingFieldForDependent(field.fieldName)],
            dependentValues: dependentPicklistValues[field.fieldName],
            dependentValuesLength:
              dependentPicklistValues[field.fieldName]?.length || 0,
          });
        }

        // Debug: Log picklist field validation for Gender
        if (field.fieldName === 'gender') {
          console.log('🔍 Rendering picklist field (gender):', {
            fieldName: field.fieldName,
            value: value,
            validationClass: validationClass,
            hasError: getFieldError(field.fieldName),
            isRequired: field.isRequired,
            fieldError: fieldError,
            isDependent,
            isControlling,
            shouldDisable,
          });
        }

        console.log('Rendering picklist field:', field.fieldName, {
          isDependent,
          isControlling,
          shouldDisable,
          availableValues: fieldPicklistValues.length,
          currentValue: value,
        });

        return (
          <>
            <Form.Select
              name={field.fieldName}
              value={value || ''}
              onChange={e => {
                handleChange(e, field.fieldName);

                // Clear error for this field when user makes a selection
                if (errors[field.fieldName]) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              required={field.isRequired}
              disabled={shouldDisable || isSaving}
              className={`${validationClass} ${isDependent ? 'dependent-field' : ''} ${shouldDisable ? 'disabled-dependent-field' : ''}`}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            >
              <option value=''>-- Select --</option>
              {shouldDisable && fieldPicklistValues.length === 0 ? (
                <option value='' disabled>
                  No values available
                </option>
              ) : (
                fieldPicklistValues.map((opt, index) => {
                  const optionKey = opt.key || opt.value_key;
                  const optionValue = opt.value || opt.value_label;
                  console.log(
                    `Rendering option ${index}: key=${optionKey}, value=${optionValue}, fullOpt=`,
                    opt
                  );
                  return (
                    <option
                      key={optionKey}
                      value={optionKey}
                      selected={opt.is_default && !value}
                    >
                      {optionValue}
                    </option>
                  );
                })
              )}
            </Form.Select>
            {isDependent && shouldDisable && !shouldDisableCurrencyPicklist && (
              <small className='text-muted'>
                {(() => {
                  const controllingFieldName = getControllingFieldForDependent(
                    field.fieldName
                  );
                  const controllingValue = formData[controllingFieldName];
                  const dependentValues =
                    dependentPicklistValues[field.fieldName];

                  // Get the controlling field label from fieldMetaByName
                  const controllingFieldMeta =
                    fieldMetaByName[controllingFieldName];
                  const controllingFieldLabel =
                    controllingFieldMeta?.label || controllingFieldName;

                  if (!controllingValue) {
                    return `Please select a value for ${controllingFieldLabel} first`;
                  } else if (dependentValues === undefined) {
                    return `Loading available values for ${field.label}...`;
                  } else if (!dependentValues || dependentValues.length === 0) {
                    return `No ${field.label} values available for selected ${controllingFieldLabel}`;
                  } else {
                    return `Please select a value for ${controllingFieldLabel} first`;
                  }
                })()}
              </small>
            )}
            {shouldDisableCurrencyPicklist && (
              <Form.Text className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                <i className="fas fa-info-circle me-1"></i>
                Currency cannot be changed because this {objectName} already has products associated with it. Please remove all products before changing the currency.
              </Form.Text>
            )}
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'textarea':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              as='textarea'
              rows={3}
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'lookup':
        console.log('## In lookup case for field:', field.fieldName);
        console.log('## Object name:', objectName);
        console.log('## Field name lowercase:', field.fieldName?.toLowerCase());

        // Special handling for related_record_id in task objects
        if (
          objectName === 'task' &&
          field.fieldName?.toLowerCase() === 'related_record_id'
        ) {
          console.log(
            '## Rendering related_record_id as lookup for task object'
          );
          console.log('## Field data:', field);
          console.log('## Form data related_object:', formData.related_object);

          const selectedRelatedObject = formData.related_object;
          if (selectedRelatedObject) {
            // Extract lookup_filter for related_record_id if available
            const { lookupFilterArray, lookupFilterLogic } = extractLookupFilter(field);

            return (
              <LookupLayout
                key={`related_record_id_${selectedRelatedObject}`}
                objectName={selectedRelatedObject}
                searchFields='name'
                displayFields='name'
                value={value}
                defaultLabel={labelData[field.fieldName] || ''}
                onChange={(id, label) =>
                  handleLookupChange(field.fieldName, id, label)
                }
                disabled={field.isReadonly || isSaving}
                isRequired={field.isRequired}
                className={validationClass}
                lookupFilter={lookupFilterArray}
                lookupFilterLogic={lookupFilterLogic}
                lookupFilterFieldValues={formData}
                scopeRecordviewToObjectName={scopeRecordviewToObjectNameForField(field)}
                recordviewIdAllowlist={recordviewAllowlistForLookupField(field)}
              />
            );
          } else {
            return (
              <Form.Control
                name={field.fieldName}
                type='text'
                value={value}
                disabled
                className='readonly-field'
                placeholder='Please select a Related Object first'
                style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize.lg,
                }}
              />
            );
          }
        }

        // Regular lookup field handling
        // Hide WhatsApp template lookup when WatConnect package is not enabled
        if (objectName === 'task' && field.fieldName?.toLowerCase() === 'whatsapp_template_id' && !hasPackage('watconnect')) {
          return null;
        }
        // Extract lookup_filter and lookup_filter_logic from field data
        const { lookupFilterArray, lookupFilterLogic } = extractLookupFilter(field);

        return (
          <LookupLayout
            objectName={field.referenceObject || field.reference_object}
            searchFields={field.lookupSearchFields}
            displayFields={field.displayFields}
            value={value}
            defaultLabel={labelData[field.fieldName] || ''}
            onChange={(id, label) =>
              handleLookupChange(field.fieldName, id, label)
            }
            disabled={field.isReadonly || isSaving}
            isRequired={field.isRequired}
            className={validationClass}
            lookupFilter={lookupFilterArray}
            lookupFilterLogic={lookupFilterLogic}
            lookupFilterFieldValues={formData}
            scopeRecordviewToObjectName={scopeRecordviewToObjectNameForField(field)}
            recordviewIdAllowlist={recordviewAllowlistForLookupField(field)}
          />
        );

      case 'date':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='date'
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'datetime':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='datetime-local'
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'time':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='time'
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'checkbox':
      case 'boolean':
        // Special handling for send_notification_to_related_record checkbox
        const isNotificationCheckbox = objectName === 'task' &&
          field.fieldName?.toLowerCase() === 'send_notification_to_related_record';

        return (
          <Form.Check
            type='checkbox'
            label=''
            checked={Boolean(formData[field.fieldName])}
            onChange={e => {
              const isChecked = e.target.checked;

              setFormData(prev => {
                const updated = {
                  ...prev,
                  [field.fieldName]: isChecked,
                };

                // If unchecking send_notification_to_related_record, clear email and phone fields
                if (isNotificationCheckbox && !isChecked) {
                  updated.related_object_email_field = '';
                  updated.related_object_phone_field = '';
                }

                return updated;
              });

              // Clear error for this field when user interacts with checkbox
              if (errors[field.fieldName]) {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[field.fieldName];
                  return newErrors;
                });
              }
            }}
            disabled={field.isReadonly || isSaving}
          />
        );
      case 'multipicklist':
        // Check if this is a dependent field
        const isMultiDependent = isDependentField(field.fieldName);
        const isMultiControlling = isControllingField(field.fieldName);
        console.log('$$ isMultiDependent: ', isMultiDependent);
        console.log('$$ isMultiControlling: ', isMultiControlling);
        console.log('$$ field.fieldName: ', field.fieldName);
        console.log('$$ field.type: ', field.type);
        console.log('$$ field.picklistValues: ', field.picklistValues);
        // Get appropriate picklist values based on dependency status
        console.log(
          '$$ dependentPicklistValues[field.fieldName]',
          dependentPicklistValues[field.fieldName]
        );
        console.log('$$ dependentPicklistValues: ', dependentPicklistValues);
        console.log(
          '$$ picklistDependencies for this field:',
          picklistDependencies.filter(
            dep =>
              dep.controlling_field_details?.name === field.fieldName ||
              dep.dependent_field_details?.name === field.fieldName
          )
        );
        let multiFieldPicklistValues;
        if (isMultiDependent) {
          // For dependent fields, show filtered values if available, otherwise show NO values
          const filteredValues = dependentPicklistValues[field.fieldName];
          if (filteredValues !== undefined) {
            // Check if it's been initialized
            if (filteredValues && filteredValues.length > 0) {
              // Ensure the values have the correct structure for MultiSelectDropdown
              multiFieldPicklistValues = filteredValues.map(val => ({
                key: val.key || val.value_key,
                value: val.value || val.value_label,
              }));
            } else {
              // No filtered values available, show empty array
              multiFieldPicklistValues = [];
            }
          } else {
            // If dependentPicklistValues is undefined, it means no controlling value is selected yet.
            // In this case, we should show NO values for the dependent field.
            multiFieldPicklistValues = [];
          }
        } else {
          // Same source as single picklist: role-filtered options from fetch (picklistValues state)
          multiFieldPicklistValues = (
            picklistValues[field.fieldName] ||
            field.picklistValues ||
            []
          ).map(val => ({
            key: val.key || val.value_key,
            value: val.value || val.value_label,
          }));
        }

        // Task notification_type: hide WhatsApp option when WatConnect package is not enabled
        if (objectName === 'task' && field.fieldName?.toLowerCase() === 'notification_type' && !hasPackage('watconnect')) {
          multiFieldPicklistValues = (multiFieldPicklistValues || []).filter(
            val => (val.key || '').toLowerCase() !== 'whatsapp'
          );
        }

        // Determine if field should be disabled
        const shouldDisableMulti =
          field.isReadonly ||
          (isMultiDependent && shouldDisableDependentField(field.fieldName));

        // Always show the MultiSelectDropdown, but disable it when no values are available
        const hasValues =
          Array.isArray(multiFieldPicklistValues) &&
          multiFieldPicklistValues.length > 0;
        const isDisabled = shouldDisableMulti || !hasValues || isSaving;

        // Determine placeholder text based on state
        let placeholderText = 'Select...';
        if (isMultiDependent && !hasValues) {
          const controllingFieldName = getControllingFieldForDependent(
            field.fieldName
          );
          const controllingValue = formData[controllingFieldName];

          // Get the controlling field label from fieldMetaByName
          const controllingFieldMeta = fieldMetaByName[controllingFieldName];
          const controllingFieldLabel =
            controllingFieldMeta?.label || controllingFieldName;

          if (!controllingValue) {
            placeholderText = `Please select ${controllingFieldLabel} first`;
          } else {
            placeholderText = `No ${field.label} values available for selected ${controllingFieldLabel}`;
          }
        } else if (!hasValues) {
          placeholderText = 'No values available';
        }

        return (
          <>
            <MultiSelectDropdown
              selectedValues={
                formData[field.fieldName]
                  ? formData[field.fieldName].split(';').filter(v => v)
                  : []
              }
              picklistValues={multiFieldPicklistValues || []}
              // Debug: Log what we're passing to MultiSelectDropdown
              onFocus={() => {
                console.log(`🔍 Multipicklist ${field.fieldName} focused:`);
                console.log(`  - Is dependent: ${isMultiDependent}`);
                console.log(`  - Is controlling: ${isMultiControlling}`);
                console.log(`  - Should disable: ${shouldDisableMulti}`);
                console.log(`  - Has values: ${hasValues}`);
                console.log(`  - Is disabled: ${isDisabled}`);
                console.log(`  - Available values:`, multiFieldPicklistValues);
                console.log(
                  `  - Value structure:`,
                  multiFieldPicklistValues?.map(v => ({
                    key: v.key,
                    value: v.value,
                  }))
                );
                console.log(
                  `  - Dependent values from state:`,
                  dependentPicklistValues[field.fieldName]
                );
              }}
              onChange={selected => {
                const semicolonSeparated = selected.join(';');

                // Use the main handleChange function to trigger dependent picklist logic
                const syntheticEvent = {
                  target: {
                    value: semicolonSeparated,
                    name: field.fieldName,
                  },
                };

                handleChange(syntheticEvent, field.fieldName);
              }}
              isRequired={field.isRequired}
              placeholder={placeholderText}
              disabled={isDisabled}
            />
            {isMultiDependent && isDisabled && (
              <small className='text-muted'>
                {(() => {
                  const controllingFieldName = getControllingFieldForDependent(
                    field.fieldName
                  );
                  const controllingValue = formData[controllingFieldName];

                  // Get the controlling field label from fieldMetaByName
                  const controllingFieldMeta =
                    fieldMetaByName[controllingFieldName];
                  const controllingFieldLabel =
                    controllingFieldMeta?.label || controllingFieldName;

                  if (!controllingValue) {
                    return `Please select a value for ${controllingFieldLabel} first`;
                  } else if (!hasValues) {
                    return `No ${field.label} values available for selected ${controllingFieldLabel}`;
                  } else {
                    return `Please select a value for ${controllingFieldLabel} first`;
                  }
                })()}
              </small>
            )}
          </>
        );
      case 'richtext':
        return (
          <div key={field.fieldName} className='mb-3'>
            <CustomRichTextEditor
              value={formData[field.fieldName] || ''}
              onChange={val => {
                setFormData({ ...formData, [field.fieldName]: val });

                // Clear error for this field when user starts typing
                if (errors[field.fieldName]) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              disabled={field.isReadonly || isSaving}
              placeholder={field.isRequired ? `${field.label} (required)` : `Enter ${field.label}`}
            />
          </div>
        );
      case 'number':
        // Get numeric constraints from field configuration
        const precision = field.precision;
        const scale = field.scale || 0;
        const maxValue = precision ? Math.pow(10, precision - scale) - 1 : null;
        const allowNegativeNumber = getAllowNegative(field);
        const minValue = maxValue !== null ? (allowNegativeNumber ? -maxValue : 0) : (allowNegativeNumber ? null : 0);

        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='number'
              value={formData[field.fieldName] || ''}
              onChange={e => {
                const val = e.target.value;
                const numVal = val === '' ? null : Number(val);

                // Always update the form data first
                setFormData({
                  ...formData,
                  [field.fieldName]: numVal,
                });

                // Validate negative when not allowed (display_config.allow_negative === false)
                if (numVal !== null && !allowNegativeNumber && numVal < 0) {
                  setErrors(prev => ({
                    ...prev,
                    [field.fieldName]: `${field.label} does not allow negative values.`,
                  }));
                  return;
                }

                // Then validate precision/scale if needed
                if (numVal !== null && precision) {
                  const validation = Validator.validateNumericPrecision(
                    numVal,
                    precision,
                    scale
                  );
                  if (!validation.isValid) {
                    setErrors(prev => ({
                      ...prev,
                      [field.fieldName]: validation.message,
                    }));
                  } else {
                    // Clear error if validation passes
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors[field.fieldName];
                      return newErrors;
                    });
                  }
                } else {
                  // Clear error for empty values or when no precision
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              placeholder={field.placeholder || 'Enter number'}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              min={minValue}
              max={maxValue}
              step={scale > 0 ? Math.pow(10, -scale) : 1}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'autonumber':
        console.log(
          'Rendering autonumber field:',
          field.fieldName,
          'with value:',
          formData[field.fieldName]
        );
        return (
          <Form.Control
            type='text'
            value={formData[field.fieldName] || ''}
            disabled
            className='readonly-field'
            placeholder='Auto-generated'
            style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.lg,
            }}
          />
        );

      case 'system':
        // Handle system fields (createddate, lastmodifieddate, createdbyid, lastmodifiedbyid)
        const systemFields = [
          'createddate',
          'lastmodifieddate',
          'createdbyid',
          'lastmodifiedbyid',
        ];
        if (systemFields.includes(field.fieldName?.toLowerCase())) {
          return (
            <Form.Control
              type='text'
              value={formData[field.fieldName] || ''}
              disabled
              className='readonly-field'
              placeholder='System field'
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
          );
        }
      // Fall through to default case for other fields

      case 'currency':
        // Get numeric constraints from field configuration
        const currencyPrecision = field.precision;
        const currencyScale = field.scale || 2; // Default to 2 decimal places for currency
        const currencyMaxValue = currencyPrecision
          ? Math.pow(10, currencyPrecision - currencyScale) - 1
          : null;
        const allowNegativeCurrency = getAllowNegative(field);
        const currencyMinValue =
          currencyMaxValue !== null
            ? (allowNegativeCurrency ? -currencyMaxValue : 0)
            : (allowNegativeCurrency ? null : 0);

        // Check if currency field should be disabled (if opportunity/quote has products)
        const isCurrencyField = field.fieldName?.toLowerCase() === 'currency';
        const shouldDisableCurrency = isCurrencyField &&
          (objectName === 'opportunity' || objectName === 'quote') &&
          hasProducts;

        // Debug logging for currency fields
        if (
          field.fieldName === 'annual_revenue' ||
          field.fieldName === 'employees'
        ) {
          console.log('🔍 Currency field rendering:', field.fieldName, {
            precision: currencyPrecision,
            scale: currencyScale,
            maxValue: currencyMaxValue,
            minValue: currencyMinValue,
            fieldData: field,
          });
        }

        return (
          <>
            <div className='input-group'>
              <span className='input-group-text'>
                {field.currencySymbol || '₹'}
              </span>
              <Form.Control
                name={field.fieldName}
                type='number'
                step={Math.pow(10, -currencyScale)}
                value={formData[field.fieldName] || ''}
                onChange={e => {
                  const val = e.target.value;
                  const numVal = val === '' ? null : parseFloat(val);

                  // Always update the form data first
                  setFormData({
                    ...formData,
                    [field.fieldName]: numVal,
                  });

                  // Validate negative when not allowed (display_config.allow_negative === false)
                  if (numVal !== null && !allowNegativeCurrency && numVal < 0) {
                    setErrors(prev => ({
                      ...prev,
                      [field.fieldName]: `${field.label} does not allow negative values.`,
                    }));
                    return;
                  }

                  // Then validate precision/scale if needed
                  if (numVal !== null && currencyPrecision) {
                    const validation = Validator.validateNumericPrecision(
                      numVal,
                      currencyPrecision,
                      currencyScale
                    );
                    if (!validation.isValid) {
                      setErrors(prev => ({
                        ...prev,
                        [field.fieldName]: validation.message,
                      }));
                    } else {
                      // Clear error if validation passes
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[field.fieldName];
                        return newErrors;
                      });
                    }
                  } else {
                    // Clear error for empty values
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors[field.fieldName];
                      return newErrors;
                    });
                  }
                }}
                required={field.isRequired}
                disabled={field.isReadonly || shouldDisableCurrency || isSaving}
                placeholder={field.placeholder || 'Enter amount'}
                className={validationClass}
                onBlur={() => handleBlur(field)}
                min={currencyMinValue}
                max={currencyMaxValue}
                style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize.lg,
                }}
              />
            </div>
            {shouldDisableCurrency && (
              <Form.Text className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                <i className="fas fa-info-circle me-1"></i>
                Currency cannot be changed because this {objectName} already has products associated with it. Please remove all products before changing the currency.
              </Form.Text>
            )}
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'calculated':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='text'
              value={value || ''}
              disabled
              className={`readonly-field ${validationClass}`}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
              placeholder='Calculated field'
            />
          </>
        );

      case 'password':
        const isPasswordVisible = visiblePasswordFields.has(field.fieldName);

        return (
          <>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Form.Control
                name={field.fieldName}
                type="text"
                value={value}
                onChange={e => handleChange(e, field.fieldName)}
                required={field.isRequired}
                disabled={field.isReadonly || isSaving}
                className={validationClass}
                onBlur={() => {
                  handleBlur(field);
                  // Auto-hide password when user leaves the field
                  if (value && isPasswordVisible) {
                    setVisiblePasswordFields(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(field.fieldName);
                      return newSet;
                    });
                  }
                }}
                onFocus={() => {
                  // Auto-show password when user focuses the field
                  if (!isPasswordVisible) {
                    setVisiblePasswordFields(prev => new Set(prev).add(field.fieldName));
                  }
                }}
                style={{
                  fontFamily: isPasswordVisible ? theme.typography.fontFamily.primary : 'monospace',
                  fontSize: theme.typography.fontSize.lg,
                  paddingRight: '40px',
                  letterSpacing: isPasswordVisible ? 'normal' : '2px',
                  textSecurity: isPasswordVisible ? 'none' : 'disc',
                  WebkitTextSecurity: isPasswordVisible ? 'none' : 'disc'
                }}
                placeholder='Enter new password (leave empty to keep existing)'
              />
              <i
                className={isPasswordVisible ? "fas fa-eye-slash" : "fas fa-eye"}
                onClick={() => {
                  if (field.isReadonly || isSaving) return;
                  setVisiblePasswordFields(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(field.fieldName)) {
                      newSet.delete(field.fieldName);
                    } else {
                      newSet.add(field.fieldName);
                    }
                    return newSet;
                  });
                }}
                style={{
                  position: 'absolute',
                  right: '12px',
                  cursor: field.isReadonly || isSaving ? 'not-allowed' : 'pointer',
                  color: theme.colors.textSecondary,
                  transition: theme.transitions.normal,
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = theme.colors.primary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = theme.colors.textSecondary;
                }}
                title={isPasswordVisible ? 'Hide password' : 'Show password'}
              ></i>
            </div>
            <Form.Text className="text-muted">
              Leave empty to keep the existing password unchanged
            </Form.Text>
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'phone':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='tel'
              value={value}
              onChange={e => {
                handleChange(e, field.fieldName);

                // Real-time validation for phone numbers
                const phoneValue = e.target.value;
                if (phoneValue && !validatePhoneNumber(phoneValue)) {
                  setErrors(prev => ({
                    ...prev,
                    [field.fieldName]: 'Invalid phone number. Please enter a valid phone number with at least 10 digits.'
                  }));
                } else {
                  // Clear error if validation passes
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field.fieldName];
                    return newErrors;
                  });
                }
              }}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              placeholder='Enter phone number (e.g., +1-234-567-8900)'
              pattern='[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,5}[-\s\.]?[0-9]{1,4}'
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            <Form.Text className="text-muted">
              Enter a valid phone number with at least 10 digits. Formats like +1-234-567-8900, (123) 456-7890, or 1234567890 are accepted.
            </Form.Text>
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'barcode':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='text'
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              placeholder='Enter value to encode as barcode (e.g., SKU, product code)'
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            <Form.Text className="text-muted">
              Enter the value to be encoded as a barcode. This will be displayed as a scannable barcode in detail view.
            </Form.Text>
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'qrcode':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              as='textarea'
              rows={3}
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              placeholder='Enter text, URL, or data to encode as QR code'
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            <Form.Text className="text-muted">
              Enter text, URL, or any data to be encoded as a QR code. This will be displayed as a scannable QR code in detail view.
            </Form.Text>
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      case 'image':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type='url'
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              placeholder='Enter image URL (e.g., https://example.com/image.jpg)'
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            <Form.Text className="text-muted">
              Enter the URL of the image. The image will be displayed with configured dimensions in detail view.
            </Form.Text>
            {value && (
              <div style={{ marginTop: '10px' }}>
                <img
                  src={value}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '4px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );

      default:
        // Special handling for related_record_id in task objects (when datatype is text)
        if (
          objectName === 'task' &&
          field.fieldName?.toLowerCase() === 'related_record_id'
        ) {
          console.log(
            '## Rendering related_record_id as lookup for task object (text type)'
          );
          console.log('## Field data:', field);
          console.log('## Form data related_object:', formData.related_object);

          const selectedRelatedObject = formData.related_object;
          if (selectedRelatedObject) {
            // Extract lookup_filter for related_record_id if available
            const { lookupFilterArray, lookupFilterLogic } = extractLookupFilter(field);

            return (
              <LookupLayout
                key={`related_record_id_${selectedRelatedObject}`}
                objectName={selectedRelatedObject}
                searchFields='name'
                displayFields='name'
                value={value}
                defaultLabel={labelData[field.fieldName] || ''}
                onChange={(id, label) =>
                  handleLookupChange(field.fieldName, id, label)
                }
                disabled={field.isReadonly || isSaving}
                isRequired={field.isRequired}
                className={validationClass}
                lookupFilter={lookupFilterArray}
                lookupFilterLogic={lookupFilterLogic}
                lookupFilterFieldValues={formData}
                scopeRecordviewToObjectName={scopeRecordviewToObjectNameForField(field)}
                recordviewIdAllowlist={recordviewAllowlistForLookupField(field)}
              />
            );
          } else {
            return (
              <Form.Control
                name={field.fieldName}
                type='text'
                value={value}
                disabled
                className='readonly-field'
                placeholder='Please select a Related Object first'
                style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize.lg,
                }}
              />
            );
          }
        }

        // Default text field handling
        return (
          <>
            <Form.Control
              name={field.fieldName}
              type={field.type}
              value={value}
              onChange={e => handleChange(e, field.fieldName)}
              required={field.isRequired}
              disabled={field.isReadonly || isSaving}
              className={validationClass}
              onBlur={() => handleBlur(field)}
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.lg,
              }}
            />
            {fieldError && (
              <div className='invalid-feedback d-block'>{fieldError}</div>
            )}
          </>
        );
    }
  };

  /**
   * Same visibility rules as main layout sections (task type, mail config, WatConnect).
   * Used for both section fields and layout "hidden" fields (otherHiddenFields).
   */
  const isFieldVisibleForEdit = f => {
    if (objectName === 'task') {
      if (
        f.fieldName?.toLowerCase() === 'whatsapp_template_id' &&
        !hasPackage('watconnect')
      ) {
        return false;
      }
      const taskType = formData?.task_type || formData?.tasktype || formData?.type;
      if (isDependentField(f.fieldName)) {
        const controllingFieldName = getControllingFieldForDependent(f.fieldName);
        if (controllingFieldName) {
          const controllingValue = formData[controllingFieldName];
          if (
            controllingValue &&
            isFieldVisible(controllingFieldName, taskType, formData)
          ) {
            return isFieldVisible(f.fieldName, taskType, formData);
          }
        }
        return isFieldVisible(f.fieldName, taskType, formData);
      }
      return isFieldVisible(f.fieldName, taskType, formData);
    }
    if (objectName === 'mail_config') {
      const mailConfigType = formData?.type || formData?.Type;
      return isMailConfigFieldVisible(f.fieldName, mailConfigType);
    }
    return true;
  };

  console.log('recordData in render:', recordData);
  console.log('Current errors state:', errors);
  return (
    <div className='edit-layout-salesforce'>
      {/* Header removed when used in modal - only show when standalone */}
      {!window.location.pathname.includes('modal') && !hideHeader && (
        <div className='edit-header'>
          <div className='edit-title'>
            <div className='title-icon'>
              <i className='fas fa-plus-circle'></i>
            </div>
            <div className='title-content'>
              <div
                className='title-subtitle'
                style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.white, // Same as AppHeader brand
                  fontSize: theme.typography.fontSize.sm,
                }}
              >
                {id ? 'EDIT' : 'NEW'}
              </div>
              <h1
                className='title-main'
                style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.white, // Same as AppHeader brand
                  fontSize: theme.typography.fontSize['3xl'],
                }}
              >
                {(recordData?.header?.titleField || objectName).toUpperCase()}
              </h1>
            </div>
          </div>
        </div>
      )}

      <div className='edit-content'>
        {/* Error Display */}
        {(error || submitError) && (
          <div
            className='alert alert-danger alert-dismissible fade show'
            role='alert'
          >
            <i className='fas fa-exclamation-triangle me-2'></i>
            {error || submitError}
            <button
              type='button'
              className='btn-close'
              onClick={() => {
                if (error) {
                  setError(null);
                } else {
                  setSubmitError(null);
                }
              }}
              aria-label='Close'
            ></button>
          </div>
        )}

        {/* Success Message Display */}
        {successMessage && (
          <div
            className='alert alert-success alert-dismissible fade show'
            role='alert'
          >
            <i className='fas fa-check-circle me-2'></i>
            {successMessage}
            <button
              type='button'
              className='btn-close'
              onClick={() => setSuccessMessage(null)}
              aria-label='Close'
            ></button>
          </div>
        )}

        {/* Loading State */}
        {!isLoaded && !error && (
          <div className='text-center p-4'>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='mt-2'>Loading record...</p>
          </div>
        )}

        {/* Form Content - Only show if loaded and no error */}
        {isLoaded && !error && (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className='salesforce-form'
            style={{ position: 'relative' }}
          >
            {/* Loading Overlay - Shows when saving */}
            {isSaving && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999,
                  cursor: 'not-allowed',
                }}
              >
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: '3rem', height: '3rem' }}
                >
                  <span className="visually-hidden">Saving...</span>
                </div>
                <div
                  style={{
                    marginTop: '1rem',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    color: theme.colors.secondary,
                  }}
                >
                  Saving...
                </div>
              </div>
            )}

            {/* General Validation Error Display - Only show after form submission attempt */}
            {hasSubmitted && hasRemainingErrors() && (
              <div
                className='alert alert-danger alert-dismissible fade show mb-4'
                role='alert'
              >
                <i className='fas fa-exclamation-triangle me-2'></i>
                <strong>Please fix the following errors:</strong>
                <ul className='mb-0 mt-2'>
                  {Object.entries(errors).map(([fieldName, errorMessage]) => {
                    // Find the field label for better error display
                    const field =
                      recordData?.details
                        ?.flatMap(s => s.fields)
                        ?.find(f => f.fieldName === fieldName) ||
                      recordData?.otherHiddenFields?.find(
                        f => f.fieldName === fieldName
                      );
                    const fieldLabel = field?.label || fieldName;
                    return (
                      <li key={fieldName}>
                        <strong>{fieldLabel}:</strong> {errorMessage}
                      </li>
                    );
                  })}
                </ul>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setErrors({});
                    setHasSubmitted(false);
                  }}
                  aria-label='Close'
                ></button>
              </div>
            )}
            {recordData?.details
              ?.filter(
                section =>
                  Array.isArray(section.fields) &&
                  section.fields.length > 0 &&
                  section.sectionName?.toLowerCase() !== 'system_info'
              )
              .map((section, idx) => {
                console.log('Rendering section:', section);

                // Pre-calculate uniqueFields to check if section should be rendered
                const seenNames = new Set();
                const uniqueFields = (section.fields || [])
                  .filter(f => {
                    const name = f?.fieldName;
                    if (!name) return false;
                    if (seenNames.has(name)) return false;
                    seenNames.add(name);
                    return true;
                  })
                  .filter(isFieldVisibleForEdit);

                // Hide section if no visible fields after filtering
                if (uniqueFields.length === 0) {
                  console.log('Hiding empty section:', section.sectionLabel || section.sectionName);
                  return null;
                }

                return (
                  <div key={idx} className='form-section'>
                    <div className='section-header'>
                      <h3
                        className='section-title'
                        style={{
                          fontFamily: theme.typography.fontFamily.primary,
                          fontWeight: theme.typography.fontWeight.semibold,
                          color: theme.colors.textPrimary,
                          fontSize: theme.typography.fontSize['2xl'],
                        }}
                      >
                        {section.sectionLabel || section.sectionName}
                      </h3>
                      <div className='section-divider'></div>
                    </div>
                    <div className='section-content'>
                      {(() => {
                        const rows = [];
                        // Get the number of columns for this section from the database
                        const sectionColumns =
                          section.layoutColumns || section.layout_columns || 2; // Default to 2 columns

                        // Create rows based on field column spans and section layout
                        let currentRow = [];
                        let currentRowWidth = 0;

                        uniqueFields.forEach(field => {
                          const meta = fieldMetaByName[field.fieldName] || {};
                          const mergedField = { ...field, ...meta };

                          if (objectName === 'mail_config') {
                            mergedField.isRequired = isMailConfigFieldRequired(
                              mergedField.fieldName,
                              formData?.type || formData?.Type,
                              mergedField.isRequired
                            );
                          }

                          // Password fields: not required when editing (leave empty to keep existing)
                          if (mergedField.type === 'password' && id && !cloneFromId) {
                            mergedField.isRequired = false;
                          }

                          // Calculate field width based on column span
                          let fieldWidth;
                          if (mergedField.columnSpan === 2) {
                            // Full width field (spans 2 columns in the section's layout)
                            fieldWidth = Math.ceil(12 / sectionColumns) * 2;
                          } else {
                            // Regular field (spans 1 column)
                            fieldWidth = Math.ceil(12 / sectionColumns);
                          }

                          // Check if this field would exceed the row width
                          if (currentRowWidth + fieldWidth > 12) {
                            // Start a new row
                            if (currentRow.length > 0) {
                              rows.push([...currentRow]);
                            }
                            currentRow = [];
                            currentRowWidth = 0;
                          }

                          // Add field to current row
                          currentRow.push(mergedField);
                          currentRowWidth += fieldWidth;

                          // Only start a new row if this field actually exceeds the available space
                          // For fields with column_span = 2, they can share rows with other fields
                          // as long as there's enough space
                        });

                        // Add any remaining fields
                        if (currentRow.length > 0) {
                          rows.push([...currentRow]);
                        }

                        return rows.map((fieldsInRow, rowIdx) => (
                          <Row className='form-row' key={rowIdx}>
                            {fieldsInRow.map((field, fIdx) => {
                              console.log(
                                '## Processing field in map:',
                                field.fieldName,
                                'type:',
                                field.type
                              );

                              // Calculate column size based on field's column span and section columns
                              let colSize;
                              if (field.columnSpan === 2) {
                                // Full width field - calculate based on section columns
                                colSize = Math.ceil(12 / sectionColumns) * 2;
                              } else {
                                // Regular field - proportional to section columns
                                colSize = Math.ceil(12 / sectionColumns);
                              }

                              return (
                                <Col
                                  key={field.fieldName || fIdx}
                                  md={colSize}
                                  className='field-column'
                                >
                                  <div
                                    className={`form-field ${getFieldError(field.fieldName) ? 'has-error' : ''}`}
                                  >
                                    <Form.Label
                                      className='field-label'
                                      style={{
                                        fontFamily:
                                          theme.typography.fontFamily.primary,
                                        fontWeight:
                                          theme.typography.fontWeight.medium,
                                        color: theme.colors.textSecondary,
                                        fontSize: theme.typography.fontSize.lg,
                                      }}
                                    >
                                      {field.label}
                                      {field.isRequired && (
                                        <span className='required-asterisk'>
                                          *
                                        </span>
                                      )}
                                      {(() => {
                                        const dependencyInfo =
                                          getDependencyInfo(field.fieldName);
                                        if (
                                          dependencyInfo &&
                                          dependencyInfo.hasDependencies
                                        ) {
                                          return (
                                            <span
                                              className='field-dependency-indicator'
                                              title={dependencyInfo.tooltip}
                                              data-bs-toggle='tooltip'
                                              data-bs-placement='top'
                                            >
                                              <i className='fas fa-info-circle'></i>
                                            </span>
                                          );
                                        }
                                        return null;
                                      })()}
                                      {field.isRequired &&
                                        (hasSubmitted ||
                                          errors[field.fieldName]) &&
                                        !formData[field.fieldName] &&
                                        errors[field.fieldName] && (
                                          <span className='required-error-indicator'>
                                            {' '}
                                            (Required)
                                          </span>
                                        )}
                                    </Form.Label>
                                    <div className='field-input'>
                                      {console.log(
                                        '## About to render field:',
                                        field.fieldName
                                      ) || renderInputField(field)}
                                    </div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        ));
                      })()}
                    </div>
                  </div>
                );
              })}

            {/* otherHiddenFields: kept in recordData / formData for save & lookups only — never show as a form section */}

            {recordData?.details
              ?.filter(
                section =>
                  section.sectionName?.toLowerCase() === 'system information' ||
                  section.sectionName?.toLowerCase() === 'system_info'
              )
              .map((section, idx) => (
                <div key={idx} className='form-section system-section'>
                  <div className='section-header'>
                    <h3
                      className='section-title'
                      style={{
                        fontFamily: theme.typography.fontFamily.primary,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.textPrimary,
                        fontSize: theme.typography.fontSize['2xl'],
                      }}
                    >
                      {section.sectionLabel || section.sectionName}
                    </h3>
                    <div className='section-divider'></div>
                  </div>
                  <div className='section-content'>
                    {(() => {
                      const rows = [];
                      // Get the number of columns for this section from the database
                      const sectionColumns =
                        section.layoutColumns || section.layout_columns || 2; // Default to 2 columns

                      // Create rows based on field column spans and section layout
                      let currentRow = [];
                      let currentRowWidth = 0;

                      section.fields.forEach(field => {
                        // Calculate field width based on column span
                        let fieldWidth;
                        if (field.columnSpan === 2) {
                          // Full width field (spans 2 columns in a 2-column layout)
                          fieldWidth = Math.ceil(12 / sectionColumns) * 2;
                        } else {
                          // Regular field (spans 1 column)
                          fieldWidth = Math.ceil(12 / sectionColumns);
                        }

                        // Check if this field would exceed the row width
                        if (currentRowWidth + fieldWidth > 12) {
                          // Start a new row
                          if (currentRow.length > 0) {
                            rows.push([...currentRow]);
                          }
                          currentRow = [];
                          currentRowWidth = 0;
                        }

                        // Add field to current row
                        currentRow.push(field);
                        currentRowWidth += fieldWidth;

                        // Only start a new row if this field actually exceeds the available space
                        // For fields with column_span = 2, they can share rows with other fields
                        // as long as there's enough space
                      });

                      // Add any remaining fields
                      if (currentRow.length > 0) {
                        rows.push([...currentRow]);
                      }

                      return rows.map((fieldsInRow, rowIdx) => (
                        <Row className='form-row' key={rowIdx}>
                          {fieldsInRow.map((field, fIdx) => {
                            console.log(
                              '## Processing field in map:',
                              field.fieldName,
                              'type:',
                              field.type
                            );

                            // Calculate column size based on field's column span and section columns
                            let colSize;
                            if (field.columnSpan === 2) {
                              // Full width field - calculate based on section columns
                              colSize = Math.ceil(12 / sectionColumns) * 2;
                            } else {
                              // Regular field - proportional to section columns
                              colSize = Math.ceil(12 / sectionColumns);
                            }

                            return (
                              <Col
                                key={field.fieldName || fIdx}
                                md={colSize}
                                className='field-column'
                              >
                                <div className='form-field'>
                                  <Form.Label
                                    className='field-label'
                                    style={{
                                      fontFamily:
                                        theme.typography.fontFamily.primary,
                                      fontWeight:
                                        theme.typography.fontWeight.medium,
                                      color: theme.colors.textSecondary,
                                      fontSize: theme.typography.fontSize.lg,
                                    }}
                                  >
                                    {field.label}
                                    {(() => {
                                      const dependencyInfo = getDependencyInfo(
                                        field.fieldName
                                      );
                                      if (
                                        dependencyInfo &&
                                        dependencyInfo.hasDependencies
                                      ) {
                                        return (
                                          <span
                                            className='field-dependency-indicator'
                                            title={dependencyInfo.tooltip}
                                            data-bs-toggle='tooltip'
                                            data-bs-placement='top'
                                          >
                                            <i className='fas fa-info-circle'></i>
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </Form.Label>
                                  <div className='field-input'>
                                    <Form.Control
                                      plaintext
                                      readOnly
                                      className='readonly-field'
                                      defaultValue={
                                        field.displayValue || field.value || '-'
                                      }
                                      style={{
                                        fontFamily:
                                          theme.typography.fontFamily.primary,
                                        fontSize: theme.typography.fontSize.lg,
                                      }}
                                    />
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      ));
                    })()}
                  </div>
                </div>
              ))}

            {/* Attendees Section - For all task types */}
            {objectName === 'task' && (
              <div className='form-section' style={{ marginTop: '20px' }}>
                <div className='section-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3
                    className='section-title'
                    style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontWeight: theme.typography.fontWeight.semibold,
                      color: theme.colors.textPrimary,
                      fontSize: theme.typography.fontSize['2xl'],
                      margin: 0,
                    }}
                  >
                    Attendees
                  </h3>
                  <Button
                    type='button'
                    variant='outline-primary'
                    size='sm'
                    onClick={addAttendee}
                    disabled={isSaving}
                    style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontSize: theme.typography.fontSize.sm,
                    }}
                  >
                    <i className='fas fa-plus me-1'></i>
                    Add Attendee
                  </Button>
                </div>
                <div className='section-body'>
                  {/* Validation message above table */}
                  <div style={{
                    marginBottom: '10px',
                    padding: '8px 12px',
                    backgroundColor: theme.colors.light,
                    borderRadius: '4px',
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.textSecondary
                  }}>
                    <span style={{ color: 'red' }}>*</span> At least one of User, Email, or Mobile is required
                  </div>
                  {attendees.length > 0 ? (
                    <div style={{ marginBottom: '15px' }}>
                      <table className='table table-bordered' style={{ marginBottom: '10px' }}>
                        <thead style={{ backgroundColor: theme.colors.light }}>
                          <tr>
                            <th style={{ width: '20%', padding: '10px' }}>User</th>
                            <th style={{ width: '20%', padding: '10px' }}>Name</th>
                            <th style={{ width: '20%', padding: '10px' }}>Email</th>
                            <th style={{ width: '20%', padding: '10px' }}>Mobile</th>
                            <th style={{ width: '20%', padding: '10px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendees.map((attendee, index) => (
                            <tr key={index}>
                              <td style={{ padding: '8px' }}>
                                <LookupLayout
                                  objectName='user'
                                  searchFields='email,firstname,lastname'
                                  displayFields='firstname,lastname'
                                  value={attendee.userid || ''}
                                  defaultLabel={attendee.userLabel || ''}
                                  onChange={(userId, userLabel) => handleAttendeeUserChange(index, userId, userLabel)}
                                  disabled={isSaving}
                                  placeholder='Search user...'
                                  isRequired={false}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Control
                                  type='text'
                                  value={attendee.name || ''}
                                  onChange={(e) => updateAttendee(index, 'name', e.target.value)}
                                  placeholder='Enter name'
                                  disabled={isSaving}
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontSize: theme.typography.fontSize.base,
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Control
                                  type='email'
                                  value={attendee.email || ''}
                                  onChange={(e) => updateAttendee(index, 'email', e.target.value)}
                                  placeholder='Enter email'
                                  disabled={isSaving}
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontSize: theme.typography.fontSize.base,
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                <Form.Control
                                  type='tel'
                                  value={attendee.mobile || ''}
                                  onChange={(e) => updateAttendee(index, 'mobile', e.target.value)}
                                  placeholder='Enter mobile number'
                                  disabled={isSaving}
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontSize: theme.typography.fontSize.base,
                                  }}
                                />
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-outline-danger'
                                  onClick={() => removeAttendee(index)}
                                  disabled={isSaving}
                                  title='Remove attendee'
                                >
                                  <i className='fas fa-trash'></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {errors['attendees'] && (
                        <div className='alert alert-danger' style={{ fontSize: theme.typography.fontSize.sm }}>
                          <i className='fas fa-exclamation-circle me-2'></i>
                          {errors['attendees']}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p style={{
                      margin: '20px 0',
                      color: theme.colors.textSecondary,
                      fontStyle: 'italic',
                      fontSize: theme.typography.fontSize.sm,
                      textAlign: 'center'
                    }}>
                      No attendees added yet. Click "Add Attendee" to add meeting participants.
                    </p>
                  )}
                </div>
              </div>
            )}

            {!hideHeader && (
              <div className='form-actions'>
                <div className='footer-actions'>
                  <Button
                    variant='outline-secondary'
                    type='button'
                    className='cancel-btn'
                    onClick={handleCancel}
                    style={{
                      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      border: `1px solid ${theme.colors.borderMedium}`,
                      background: theme.colors.white,
                      color: theme.colors.textSecondary,
                      borderRadius: theme.borderRadius.sm,
                      transition: theme.transitions.normal,
                      minWidth: '80px',
                    }}
                    onMouseEnter={e => {
                      e.target.style.background = theme.colors.light;
                      e.target.style.borderColor = theme.colors.borderDark;
                      e.target.style.color = theme.colors.textPrimary;
                    }}
                    onMouseLeave={e => {
                      e.target.style.background = theme.colors.white;
                      e.target.style.borderColor = theme.colors.borderMedium;
                      e.target.style.color = theme.colors.textSecondary;
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='outline-primary'
                    type='button'
                    className='save-new-btn'
                    onClick={handleSaveAndNew}
                    disabled={isSaving}
                    style={{
                      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      border: `1px solid ${theme.colors.secondary}`,
                      background: theme.colors.white,
                      color: theme.colors.secondary,
                      borderRadius: theme.borderRadius.sm,
                      transition: theme.transitions.normal,
                      minWidth: '100px',
                      opacity: isSaving ? 0.6 : 1,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={e => {
                      if (!isSaving) {
                        e.target.style.background = theme.colors.secondary;
                        e.target.style.color = theme.colors.white;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSaving) {
                        e.target.style.background = theme.colors.white;
                        e.target.style.color = theme.colors.secondary;
                      }
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save & New'}
                  </Button>
                  <Button
                    variant='primary'
                    type='submit'
                    className='save-btn'
                    disabled={isSaving}
                    style={{
                      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      background: theme.colors.secondary,
                      border: `1px solid ${theme.colors.secondary}`,
                      color: theme.colors.white,
                      borderRadius: theme.borderRadius.sm,
                      transition: theme.transitions.normal,
                      minWidth: '80px',
                      opacity: isSaving ? 0.6 : 1,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={e => {
                      if (!isSaving) {
                        e.target.style.background = theme.colors.secondaryDark;
                        e.target.style.borderColor = theme.colors.secondaryDark;
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = `0 2px 4px rgba(0, 123, 255, 0.2)`;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSaving) {
                        e.target.style.background = theme.colors.secondary;
                        e.target.style.borderColor = theme.colors.secondary;
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>

      {/* SalesforceToast for notifications */}
      <SalesforceToast
        show={toast.show}
        message={toast.message}
        type={toast.variant === 'danger' ? 'error' : toast.variant}
        onClose={closeToast}
        duration={5000}
        position="top-center"
      />
    </div>
  );
};

export default EditLayout;
